import React, {
  CSSProperties,
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination2";
import {
  CreativeReportAd,
  CreativeReportColumn,
  CreativeReportRow,
  CreativeReportMediaItem,
  Creative,
  VideoRetentionData,
  ViewMode,
  ServerPaginationState,
  AdDetailResponse,
} from "../../types";
import { CreativeReportMetadata, ColumnOption } from "@/types";
import {
  Column,
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Row,
  Table as TableType,
  Cell,
} from "@tanstack/react-table";
import { Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getFirstMedia } from "../CreativeReportGroupCards";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import { LuLoaderCircle } from "react-icons/lu";
import { Skeleton } from "@/components/ui/skeleton";
import CreativeGalleryPreview from "../preview/CreativeGalleryPreview";
import { debounce } from "lodash";
import { useGlobalStore } from "@/GlobalStore";
import { cn } from "@/lib/utils";
import { IconAlertTriangle, IconDatabase } from "@tabler/icons-react";
import MarkdownFormatter from "@/components/common/markdown/MarkdownFormatter";
import {
  RiArrowDownLine,
  RiArrowLeftWideLine,
  RiArrowRightWideLine,
  RiArrowUpDownLine,
  RiArrowUpLine,
  RiArrowUpWideLine,
  RiInformationLine,
  RiColorFilterLine,
  RiStackLine,
  RiPantoneLine,
  RiEyeLine,
  RiImage2Line,
  RiMovieLine,
} from "@remixicon/react";
import SelectDropdown from "@/components/common/dropdown/SelectDropdown";
import MediaIcon from "@/components/common/MediaIcon";
import { SqButton } from "@/components/squircle-ui/button";
import { getLabelColors, getPlatformIcon } from "@/screens/utils/Helpers";
import { formatDateRange } from "@/utils/dateUtils";
import PercentageText from "@/screens/creatives/components/table/PercentageText";
import { formatNumberWithPrefixSuffix } from "@/screens/creatives/components/table/cellFormatters";
import MiscellaneousPrimaryCell from "./cell/MiscellaneousPrimaryCell";
import {
  RiSearchLine,
  RiCloseLine,
  RiAddLargeLine,
  RiChatAiLine,
  RiCheckLine,
  RiDraggable,
} from "@remixicon/react";
import MultiMediaIcon from "@/components/common/icons/MultiMediaIcon";
import Metrics from "@/components/common/metrics";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { PRIMARY_COLUMN_JOINER } from "../../constants";
import CopyButton from "@/components/common/CopyButton";
import { useAttachedCreativesStore } from "../../store/attachedCreativesStore";
import WinningStar from "@/components/common/icons/WinningStar";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";

// Common components and utilities
interface CommonButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "icon" | "outline";
  hasOutline?: boolean;
  bgColor?: string;
  hoverBgColor?: string;
  size?: "default" | "sm" | "md" | "lg" | "xl";
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
}

const CommonButton = React.forwardRef<HTMLButtonElement, CommonButtonProps>(
  (
    {
      variant = "icon",
      hasOutline = true,
      bgColor = "white",
      hoverBgColor = "#FBFBFB",
      className = "h-7 w-7 shrink-0",
      size,
      shadow,
      onClick,
      children,
      ...props
    },
    ref,
  ) => (
    <SqButton
      ref={ref}
      variant={variant}
      hasOutline={hasOutline}
      bgColor={bgColor}
      hoverBgColor={hoverBgColor}
      size={size}
      shadow={shadow}
      className={className}
      onClick={onClick}
      {...props}
    >
      {children}
    </SqButton>
  ),
);

CommonButton.displayName = "CommonButton";

const SortableTableHead = ({
  id,
  disabled = false,
  children,
  style,
  className,
  colSpan,
}: {
  id: string;
  disabled?: boolean;
  children: React.ReactNode;
  style?: CSSProperties;
  className?: string;
  colSpan?: number;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  const combinedStyle: CSSProperties = {
    ...style,
    transform: CSS.Translate.toString(transform),
    transition: transition || undefined,
    ...(isDragging && { opacity: 0.35, zIndex: 100, background: "#f0f4ff" }),
  };

  return (
    <TableHead
      ref={setNodeRef}
      style={combinedStyle}
      className={cn(className, !disabled && "cursor-grab active:cursor-grabbing")}
      colSpan={colSpan}
      {...attributes}
      {...listeners}
    >
      {children}
    </TableHead>
  );
};

const SortButton = ({
  columnId,
  sortState,
  handleSortChange,
  column,
  className = "h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity",
}: {
  columnId: string;
  sortState: { column: string | null; direction: "asc" | "desc" | false };
  handleSortChange: (state: {
    column: string | null;
    direction: "asc" | "desc" | false;
  }) => void;
  column: Column<CreativeReportRow, unknown>;
  className?: string;
}) => (
  <SqTooltipProvider delayDuration={100}>
    <SqTooltip>
      <SqTooltipTrigger asChild>
        <CommonButton
          className={cn("cursor-pointer", className)}
          onClick={() => {
            const currentSort =
              sortState.column === columnId ? sortState.direction : false;
            if (currentSort === false) {
              handleSortChange({ column: columnId, direction: "desc" });
              column.toggleSorting(true);
            } else if (currentSort === "desc") {
              handleSortChange({ column: columnId, direction: "asc" });
              column.toggleSorting(false);
            } else {
              handleSortChange({ column: null, direction: false });
              column.clearSorting();
            }
          }}
        >
          {(() => {
            const currentSort =
              sortState.column === columnId ? sortState.direction : false;
            if (!currentSort) {
              return <RiArrowUpDownLine size={14} color="#9AA3BD" />;
            } else if (currentSort === "asc") {
              return <RiArrowDownLine size={14} color="#9AA3BD" />;
            } else {
              return <RiArrowUpLine size={14} color="#9AA3BD" />;
            }
          })()}
        </CommonButton>
      </SqTooltipTrigger>
      <SqTooltipContent align="start" side="bottom" portal>
        {(() => {
          const currentSort = sortState.column === columnId ? sortState.direction : false;
          if (!currentSort) {
            return "Sort";
          } else if (currentSort === "asc") {
            return "Ascending";
          } else {
            return "Descending";
          }
        })()}
      </SqTooltipContent>
    </SqTooltip>
  </SqTooltipProvider>
);

const ExpandCollapseButton = ({
  isExpanded,
  onClick,
  className = "h-7 w-7 shrink-0 mr-2 squircle-fill-[#FFFFFF] opacity-0 group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity",
}: {
  isExpanded: boolean;
  onClick: (e: React.MouseEvent) => void;
  className?: string;
}) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <CommonButton className={className} onClick={onClick}>
          {isExpanded ? (
            <RiArrowLeftWideLine size={14} color="#9AA3BD" />
          ) : (
            <RiArrowRightWideLine size={14} color="#9AA3BD" />
          )}
        </CommonButton>
      </TooltipTrigger>
      <Portal>
        <TooltipContent side="bottom" align="start">
          {isExpanded ? "Collapse" : "Expand"}
        </TooltipContent>
      </Portal>
    </Tooltip>
  </TooltipProvider>
);

// Common utility functions
const getColumnAlignment = (
  col: CreativeReportColumn,
  columns: CreativeReportColumn[],
) => {
  const isNumber = col.column_type === "number" || col.data_type === "number";
  const isNode = (col.column_type as string) === "node";
  const isNodeGroup = (col.column_type as string) === "node_group";

  if (isNumber) return "right";
  if (isNode && col.data_type === "number") return "right";
  if (isNodeGroup) {
    const nodeGroupDataType = col.data_type;
    if (nodeGroupDataType === "number") return "right";
    if (!nodeGroupDataType) {
      const defaultKey = col.default_key;
      if (defaultKey) {
        const defaultKeyColumn = columns.find((c) => c.key === defaultKey);
        if (defaultKeyColumn && defaultKeyColumn.data_type === "number") {
          return "right";
        }
      }
    }
  }
  return "left";
};

const getColumnSize = (
  col: CreativeReportColumn,
  isFirst: boolean,
  hasSequenceData: boolean,
  containerWidth: number,
) => {
  const isNumber = col.column_type === "number" || col.data_type === "number";

  if (isFirst) {
    return hasSequenceData ? containerWidth * 0.5 : 320;
  }

  if (isNumber || col.column_type === "text") return 147;
  if (col.column_type === "text_ai_label") return 147 * 1.5;
  if (col.column_type === "text_ai_description") return 147 * 2;
  if (col.column_type === "custom") return 260;
  if ((col.column_type as string) === "node_group") return 147;
  if ((col.column_type as string) === "node") return 147;
  return 147;
};

const formatValueWithSuffix = (
  value: number,
  prefix: string,
  suffix: string,
  totalAds?: number | null,
  isChangeColumn: boolean = false,
) => {
  let formatted = formatNumberWithPrefixSuffix(value, prefix, suffix);
  // For change columns, replace {total_ads} with "Ads" instead of the actual total
  if (isChangeColumn && suffix && suffix.includes("{total_ads}")) {
    formatted = formatNumberWithPrefixSuffix(value, prefix, " Ads");
  } else if (
    suffix &&
    suffix.includes("{total_ads}") &&
    totalAds !== undefined &&
    totalAds !== null
  ) {
    formatted = formatted.replace("{total_ads}", String(totalAds));
  }

  return formatted;
};

const getTotalAdsForColumn = (
  row: CreativeReportRow | Omit<CreativeReportRow, "ads">,
  columnId: string,
) => {
  if (columnId.endsWith("_previous")) {
    return (row as Record<string, unknown>).total_ads_previous as
      | number
      | null
      | undefined;
  } else if (columnId.endsWith("_change")) {
    return (row as Record<string, unknown>).total_ads_change as number | null | undefined;
  } else {
    return (row as Record<string, unknown>).total_ads as number | null | undefined;
  }
};

// Common column type checking functions
const isColumnNumber = (col: CreativeReportColumn) =>
  col.column_type === "number" || col.data_type === "number";

const isColumnNode = (col: CreativeReportColumn) =>
  (col.column_type as string) === "node";

const isColumnNodeGroup = (col: CreativeReportColumn) =>
  (col.column_type as string) === "node_group";

const isColumnSecondary = (columnId: string) =>
  columnId.endsWith("_previous") || columnId.endsWith("_change");

const isColumnChange = (columnId: string) => columnId.endsWith("_change");

const isColumnExpanded = (columnId: string, expandedHeaders: Record<string, boolean>) =>
  expandedHeaders[columnId.replace(/(_previous|_change)$/, "")];

const shouldShowExpandButton = (col: CreativeReportColumn, hasCompareDate: boolean) => {
  const isNumber = isColumnNumber(col);
  const isNode = isColumnNode(col);
  return (
    (isNumber && hasCompareDate) ||
    (isNode && col.data_type === "number" && hasCompareDate)
  );
};

const shouldShowNodeGroupExpandButton = (col: CreativeReportColumn) => {
  return isColumnNodeGroup(col) && col.children && col.children.length > 0;
};

const SPACER = "spacer";

// Scrollable container with fade effects
export const ScrollableFadeContainer = ({
  sequenceData,
  uniqueLabels,
  disableHoverGradient = false,
  isScrollable = true,
  viewMode = "sequence",
}: {
  sequenceData: Array<{ label: string; order: number | null }>;
  uniqueLabels?: string[];
  disableHoverGradient?: boolean;
  isScrollable?: boolean;
  viewMode?: "multiple" | "sequence";
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!scrollRef.current || !isScrollable) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    // Show left fade if not at the very beginning
    setShowLeftFade(scrollLeft > 0);

    // Show right fade if not at the very end
    setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
  }, [isScrollable]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !isScrollable) return;

    // Check initial scroll position
    checkScrollPosition();

    // Add scroll listener
    scrollElement.addEventListener("scroll", checkScrollPosition);

    // Check again after content loads
    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(scrollElement);

    return () => {
      scrollElement.removeEventListener("scroll", checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [checkScrollPosition, isScrollable]);

  // Process data based on view mode
  const processedData = useMemo(() => {
    if (viewMode === "multiple") {
      // Remove duplicates and don't show order numbers
      const uniqueItems = sequenceData.reduce(
        (acc: Array<{ label: string; order: number | null }>, item) => {
          if (!acc.some((existing) => existing.label === item.label)) {
            acc.push(item);
          }
          return acc;
        },
        [] as Array<{ label: string; order: number }>,
      );
      return uniqueItems;
    }
    // For sequence mode, keep all items with order numbers
    return sequenceData;
  }, [sequenceData, viewMode]);

  const content = (
    <div
      ref={scrollRef}
      className={`flex gap-1 mt-1 ${isScrollable ? "overflow-x-auto scrollbar-hide" : "flex-wrap"}`}
    >
      {processedData.map((item, index) => {
        // Use getLabelColors function from Helpers to get consistent colors
        const candidateIndex = uniqueLabels?.indexOf(item.label);
        const safeIndex =
          typeof candidateIndex === "number" && candidateIndex >= 0
            ? candidateIndex
            : index;
        const colors = getLabelColors(safeIndex);

        return (
          <div
            key={viewMode === "multiple" ? item.label : `${item.order}-${item.label}`}
            className="h-7 min-w-max flex items-center gap-[6px] px-2 py-1 rounded-[6px] squircle squircle-smooth-[1] squircle-radius-[6px] squircle-outline-[1px] text-[11px]"
            style={
              {
                backgroundColor: colors.background,
                color: colors.text,
                "--squircle-fill": colors.border,
              } as CSSProperties
            }
          >
            {viewMode === "sequence" && <span className="">{item.order}.</span>}
            <span className="">{item.label}</span>
          </div>
        );
      })}
    </div>
  );

  if (!isScrollable) {
    return content;
  }

  return (
    <div className="relative">
      {/* Left fade overlay */}
      {showLeftFade && (
        <div
          className="absolute left-0 top-0 bottom-0 w-[12px] z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
          }}
        >
          {!disableHoverGradient && (
            <div
              className="absolute inset-0 opacity-0 group-hover/row:opacity-100"
              style={{
                background:
                  "linear-gradient(90deg, #FBFBFB 6.25%, rgba(251, 251, 251, 0.00) 100%)",
              }}
            />
          )}
        </div>
      )}

      {/* Right fade overlay */}
      {showRightFade && (
        <div
          className="absolute right-0 top-0 bottom-0 w-[12px] z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(270deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
          }}
        >
          {!disableHoverGradient && (
            <div
              className="absolute inset-0 opacity-0 group-hover/row:opacity-100"
              style={{
                background:
                  "linear-gradient(270deg, #FBFBFB 6.25%, rgba(251, 251, 251, 0.00) 100%)",
              }}
            />
          )}
        </div>
      )}

      {/* Scrollable content */}
      {content}
    </div>
  );
};

const RenderPinnedCell = ({
  text,
  mediaItem,
  onRightPanelClick,
  mediaType,
  total_ads,
  openGalleryView,
  hideGalleryAction,
  viewMode,
  rowIndex,
  hideRightPanelTrigger,
  sequenceData,
  uniqueLabels,
  showAdsText,
  previous_total_ads,
  allAdNames,
  sectionId,
  creativeData,
  groupByKey,
  sectionMetricLabels,
  columns,
  rowData,
  isAdsLoading,
}: {
  text: React.ReactNode;
  mediaItem: CreativeReportMediaItem;
  onRightPanelClick?: () => void;
  mediaType: string;
  total_ads?: number;
  previous_total_ads?: number;
  openGalleryView: () => void;
  hideGalleryAction: boolean;
  viewMode: ViewMode;
  rowIndex: number;
  hideRightPanelTrigger: boolean;
  sequenceData?: Array<{ label: string; order: number }>;
  uniqueLabels?: string[];
  showAdsText?: string;
  allAdNames?: string[];
  sectionId?: string;
  creativeData?: {
    creative_id: string;
    ad_id: string;
    thumbnail_url?: string;
    type: "image" | "video" | "mixed";
    ad_name?: string;
    media_url: string;
  };
  groupByKey?: string;
  sectionMetricLabels?: { id: string; label: string }[];
  columns?: CreativeReportColumn[];
  rowData?: CreativeReportRow;
  isAdsLoading?: boolean;
}) => {
  const { isCreativeAttached, attachCreativeAndOpenChat, detachAndMaybeCloseChat } =
    useAttachedCreativesStore();
  const [hasImageError, setHasImageError] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Get the ad name for this row to determine color (section-wise) for line graph view
  const currentAdName = allAdNames?.[rowIndex];
  const leftEdgeColor = useGlobalStore((state) => {
    if (viewMode === "line_chart_view" && currentAdName && sectionId) {
      return state.adNameColorMapBySection[sectionId]?.[currentAdName];
    }
    return undefined;
  });

  // Get selected account for winning criteria
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);

  // Get winning criteria stars for the current row
  const { displayedStars, remainingCount, winningCriteriaNames } = useMemo(() => {
    // Only show stars when groupByKey is "creative_name"
    if (groupByKey !== "creative_name") {
      return { displayedStars: [], remainingCount: 0, winningCriteriaNames: [] };
    }

    const winningFilters = selectedAccount?.additional_info?.winning_filters;
    if (!winningFilters || !columns || !rowData) {
      return { displayedStars: [], remainingCount: 0, winningCriteriaNames: [] };
    }

    // Find winning criteria columns
    const winningColumns = columns.filter(
      (col) =>
        (col as CreativeReportColumn & { winning_criteria_name?: string })
          .winning_criteria_name,
    );

    // Get winning criteria data (non-zero values only)
    const winningCriteriaData = winningColumns
      .filter((col) => {
        const value = (rowData as Record<string, unknown>)[col.key];
        // Check if value is a number and greater than 0
        return typeof value === "number" && value > 0;
      })
      .map((col) => {
        const winningCriteriaName = (
          col as CreativeReportColumn & {
            winning_criteria_name?: string;
          }
        ).winning_criteria_name;
        if (!winningCriteriaName) return null;

        // Find matching criteria in winning_filters
        const criteria = winningFilters
          .flatMap((section) => section.criterias)
          .find((c) => c.criteria_name === winningCriteriaName);

        const colorCode = (
          criteria as { criteria_name: string; color_code?: string } | undefined
        )?.color_code;

        return {
          criteriaName: winningCriteriaName,
          colorCode,
        };
      })
      .filter(
        (item): item is { criteriaName: string; colorCode: string } =>
          item !== null && Boolean(item.colorCode),
      );

    // Remove duplicates by criteria name (keep first occurrence)
    const uniqueCriteria = Array.from(
      new Map(winningCriteriaData.map((item) => [item.criteriaName, item])).values(),
    );

    // Extract criteria names
    const criteriaNames = uniqueCriteria.map((item) => item.criteriaName);

    // Determine how many stars to display
    const totalStars = uniqueCriteria.length;
    const starsToShow = totalStars > 3 ? 2 : totalStars;

    // Create displayed stars with overlap
    const displayedStars = uniqueCriteria.slice(0, starsToShow).map((item, index) => (
      <div
        key={`${item.colorCode}-${index}`}
        style={{
          marginLeft: index > 0 ? "-6px" : "0",
        }}
      >
        <WinningStar color={item.colorCode} size={20} />
      </div>
    ));

    // Calculate remaining count
    const remainingCount = totalStars > 3 ? totalStars - 2 : 0;

    return { displayedStars, remainingCount, winningCriteriaNames: criteriaNames };
  }, [groupByKey, columns, rowData, selectedAccount]);
  return (
    <SqTooltipProvider delayDuration={300}>
      <div className="flex gap-3 items-center w-full relative">
        {/* Left edge color bar for line graph view */}
        {leftEdgeColor && (
          <div
            className="absolute h-full -left-2 top-0 bottom-0 w-[4px] z-10 rounded-r-[8px]"
            style={{
              backgroundColor: leftEdgeColor,
            }}
          />
        )}
        {/* Winning stars annotation - positioned above thumbnail, top-left */}
        {displayedStars.length > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute top-[-4px] left-[-4px] h-[24px] flex items-center cursor-pointer"
                  style={{ zIndex: 1 }}
                >
                  <div className="flex items-center">
                    {displayedStars}
                    {remainingCount > 0 && (
                      <div
                        className="flex items-center justify-center rounded-full"
                        style={{
                          width: "24px",
                          height: "24px",
                          border: "1px solid #CCD1DE",
                          background: "#FFF",
                          fontSize: "11px",
                          fontWeight: 500,
                          lineHeight: "normal",
                          color: "rgba(2, 24, 89, 0.80)",
                          marginLeft: displayedStars.length > 0 ? "-6px" : "0",
                        }}
                      >
                        +{remainingCount}
                      </div>
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <Portal>
                <TooltipContent
                  className="max-w-[300px]"
                  side="top"
                  avoidCollisions
                  collisionPadding={10}
                >
                  <div className="whitespace-pre-line">
                    Winner:{"\n"}
                    {winningCriteriaNames
                      .reduce<string[]>((acc, name, index) => {
                        if (index % 2 === 0) {
                          acc.push(name);
                        } else {
                          acc[acc.length - 1] += `, ${name}`;
                        }
                        if (index < winningCriteriaNames.length - 1) {
                          if (index % 2 === 1) {
                            acc[acc.length - 1] += ",";
                          }
                        }
                        return acc;
                      }, [])
                      .join("\n")}
                  </div>
                </TooltipContent>
              </Portal>
            </Tooltip>
          </TooltipProvider>
        )}
        <div className="relative">
          {/* Thumbnail container */}
          <div
            className={`relative group/thumbnail ${isAdsLoading ? "cursor-default" : "cursor-pointer"}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isAdsLoading) openGalleryView();
            }}
          >
            <div className="flex items-center bg-transparent justify-center min-w-[60px] w-[60px] h-[60px] overflow-hidden rounded-[8px]">
              {mediaItem && !hasImageError ? (
                mediaItem.type === "video" && mediaItem.thumbnail ? (
                  <img
                    src={mediaItem.thumbnail}
                    alt="Video thumbnail"
                    loading="lazy"
                    className="w-full h-full object-cover rounded-[8px]"
                    onError={() => setHasImageError(true)}
                  />
                ) : mediaItem.url ? (
                  <img
                    src={mediaItem.url}
                    alt="Image"
                    loading="lazy"
                    className="w-full h-full object-cover rounded-[8px]"
                    onError={() => setHasImageError(true)}
                  />
                ) : (
                  <Image size={32} strokeWidth={1.5} className="text-[#B5BCCB]" />
                )
              ) : (
                <Image size={32} strokeWidth={1.5} className="text-[#B5BCCB]" />
              )}
            </div>
            <div className="absolute inset-0 flex items-end justify-end p-[6px] opacity-100 group-hover/thumbnail:opacity-0">
              <MediaIcon type={mediaType as "image" | "video" | "mixed"} />
            </div>
            <div
              className={`absolute inset-0 bg-black/40 opacity-0 ${!hideGalleryAction && "group-hover/thumbnail:opacity-100"} transition-opacity duration-200 flex items-center justify-center rounded-[8px]`}
            >
              <RiPantoneLine size={24} className="text-white" />
            </div>
          </div>
        </div>
        <div className="overflow-hidden flex-1 flex flex-col gap-2">
          {sequenceData && sequenceData.length > 0 ? (
            <ScrollableFadeContainer
              sequenceData={sequenceData}
              uniqueLabels={uniqueLabels}
            />
          ) : (
            <div className="w-full overflow-hidden group flex items-center gap-1">
              <SqTooltipProvider delayDuration={300}>
                <SqTooltip>
                  <SqTooltipTrigger className="flex-1 overflow-hidden">
                    <p
                      role="button"
                      tabIndex={0}
                      onClick={onRightPanelClick}
                      className={`w-full whitespace-nowrap text-[12px] truncate text-left text-brand-primary-new/80 group-hover/row:text-brand-primary-new font-medium leading-5`}
                    >
                      {text}
                    </p>
                  </SqTooltipTrigger>
                  <SqTooltipContent
                    side="bottom"
                    align="start"
                    className={`text-[12px] max-w-[340px] text-left z-[50]`}
                    avoidCollisions
                    sideOffset={10}
                    alignOffset={25}
                    collisionPadding={10}
                    portal
                  >
                    <p className="text-[12px] text-left text-white break-all">{text}</p>
                  </SqTooltipContent>
                </SqTooltip>
              </SqTooltipProvider>
              <CopyButton text={text} />
            </div>
          )}
          <div className="w-full flex justify-start items-center gap-[6px]">
            {total_ads ? (
              <Button
                variant="sq-outline"
                size={"sm"}
                disabled={isAdsLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  openGalleryView();
                }}
                className={cn(
                  `text-[12px] gap-[6px] bg-white flex items-center py-1 pl-2 shadow-sm`,
                  !isNaN(Number(previous_total_ads)) && "pr-1",
                )}
              >
                <SqTooltip>
                  <SqTooltipTrigger className="flex items-center gap-[6px] cursor-pointer">
                    {isAdsLoading ? (
                      <LuLoaderCircle
                        size={14}
                        className="text-brand-primary-400 shrink-0 animate-spin"
                      />
                    ) : (
                      <RiPantoneLine
                        size={14}
                        className="text-brand-primary-400 shrink-0"
                      />
                    )}
                    <span className="mt-[1px] overflow-hidden text-brand-primary-600 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px]">
                      {showAdsText
                        ? showAdsText
                        : `${total_ads} ${total_ads === 1 ? "Ad" : "Ads"}`}
                    </span>
                  </SqTooltipTrigger>
                  <SqTooltipContent
                    side="bottom"
                    align="start"
                    sideOffset={5}
                    portal={true}
                  >
                    Show Ads
                  </SqTooltipContent>
                </SqTooltip>
                {!isNaN(Number(previous_total_ads)) &&
                  previous_total_ads !== undefined && (
                    <SqTooltip>
                      <SqTooltipTrigger className="cursor-pointer">
                        <div
                          className={`h-[22px] px-1  min-w-[22px] flex items-center justify-center squircle-mask squircle-radius-[4px]  ${
                            total_ads - previous_total_ads > 0
                              ? "!bg-[#F0F9F8] text-[#68C7B9]"
                              : "!bg-[#F6F6F6] text-[#02185966]"
                          }`}
                        >
                          {total_ads - previous_total_ads > 0
                            ? `+${total_ads - previous_total_ads}`
                            : total_ads - previous_total_ads}
                        </div>
                      </SqTooltipTrigger>
                      <SqTooltipContent
                        side="bottom"
                        align="start"
                        sideOffset={5}
                        portal={true}
                      >
                        {total_ads - previous_total_ads > 0
                          ? `${total_ads - previous_total_ads} more ads than in the last period.`
                          : total_ads - previous_total_ads < 0
                            ? `${previous_total_ads - total_ads} less ads than in the last period.`
                            : "Same number of ads as last period."}
                      </SqTooltipContent>
                    </SqTooltip>
                  )}
              </Button>
            ) : (
              ""
            )}
            {!hideRightPanelTrigger && (
              <SqTooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                <SqTooltipTrigger asChild>
                  <SqButton
                    variant="outline"
                    size={"sm"}
                    shadow={"sm"}
                    onClick={onRightPanelClick}
                    onMouseLeave={() => {
                      if (tooltipOpen) {
                        setTooltipOpen(false);
                      }
                    }}
                    className="text-brand-primary-600 cursor-pointer opacity-0 group-hover/row:opacity-100 group-hover/row:pointer-events-auto"
                  >
                    Ad breakdown
                  </SqButton>
                </SqTooltipTrigger>
                <SqTooltipContent side="bottom" align="start" portal>
                  Ad breakdown
                </SqTooltipContent>
              </SqTooltip>
            )}
            <div className="w-full flex items-center justify-end">
              {groupByKey === "creative_name" &&
                creativeData &&
                sectionId &&
                (() => {
                  const isAttached = isCreativeAttached(
                    creativeData.creative_id,
                    sectionId,
                  );
                  return (
                    <SqTooltip>
                      <SqTooltipTrigger asChild>
                        <SqButton
                          variant={isAttached ? "icon" : "outline"}
                          size="sm"
                          shadow="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isAttached) {
                              if (creativeData && sectionId) {
                                detachAndMaybeCloseChat(
                                  creativeData.creative_id,
                                  sectionId,
                                );
                              }
                            } else {
                              if (creativeData && sectionId) {
                                attachCreativeAndOpenChat(
                                  {
                                    creative_id: creativeData.creative_id,
                                    ad_id: creativeData.ad_id,
                                    section_id: sectionId,
                                    thumbnail_url: creativeData.thumbnail_url,
                                    type: creativeData.type,
                                    ad_name: creativeData.ad_name,
                                    media_url: creativeData.media_url || "",
                                  },
                                  groupByKey,
                                  sectionMetricLabels,
                                );
                              }
                            }
                          }}
                          className={cn(
                            "cursor-pointer h-7 w-7 flex items-center justify-center transition-opacity",
                            isAttached
                              ? "opacity-100 pointer-events-auto squircle-mask !bg-[linear-gradient(90deg,_rgba(255,87,26,0.7)_0%,_rgba(255,98,146,0.7)_100%)] hover:bg-[linear-gradient(90deg,_rgba(255,87,26,0.8)_0%,_rgba(255,98,146,0.8)_100%)]"
                              : "opacity-0 group-hover/row:opacity-100 pointer-events-none group-hover/row:pointer-events-auto bg-white hover:bg-[#FBFBFB]",
                          )}
                        >
                          {isAttached ? (
                            <RiCheckLine size={14} className="text-white shrink-0" />
                          ) : (
                            <RiChatAiLine
                              size={14}
                              className={cn(
                                "shrink-0",
                                isAttached ? "text-white" : "text-brand-orange",
                              )}
                            />
                          )}
                        </SqButton>
                      </SqTooltipTrigger>
                      <SqTooltipContent side="bottom" align="start" portal>
                        {isAttached
                          ? "Remove this creative"
                          : "Interact with the creative and performance data for this range and all time."}
                      </SqTooltipContent>
                    </SqTooltip>
                  );
                })()}
            </div>
          </div>
        </div>
      </div>
    </SqTooltipProvider>
  );
};

interface CreativeTableColumnMeta {
  align: "left" | "right" | "center";
  label: string;
  type: string;
  dataType: string;
  suffix: string;
  prefix: string;
  info?: string | null;
  warning?: string | null;
  uniqueLabels?: string[];
  labelType?: "unique" | "multiple";
}

interface CreativeTableProps {
  data: CreativeReportRow[];
  columns: CreativeReportColumn[];
  ad_columns?: CreativeReportColumn[];
  breakdown_columns?: CreativeReportColumn[];
  setGroupSheetOpen?: (open: boolean) => void;
  setSelectedAdGroup?: (adGroup: CreativeReportRow) => void;
  onOpenGroupSheetWithMode?: (
    row: CreativeReportRow,
    mode: "table_view" | "creative_view",
  ) => void;
  setPreviewMediaItem?: (mediaItem: CreativeReportMediaItem) => void;
  totalRow?: Omit<CreativeReportRow, "ads">;
  uniqueLabels?: string[];
  status?: "success" | "pending" | "partial" | "failed";
  partialColumns?: {
    label_config?: Array<{
      key: string;
      status: string;
      message: string;
    }>;
    description_config?: Array<{
      key: string;
      status: string;
      message: string;
    }>;
  };
  preservePaginationState?: boolean;
  initialColumnSizing?: Record<string, number>;
  initialPageSize?: number;
  onColumnResize?: (columnSizing: Record<string, number>) => void;
  onSort?: (sortState: {
    column: string | null;
    direction: "asc" | "desc" | false;
  }) => void;
  initialSortState?: {
    column: string | null;
    direction: "asc" | "desc" | false;
  };
  viewMode?: ViewMode;
  sectionId?: string;
  groupType?: string;
  creativeType?: string;
  labelView?: string;
  labelConfigId?: string;
  accountId?: string;
  hideRightPanelTrigger?: boolean;
  CustomCell?: React.ComponentType<{ cell: Cell<CreativeReportRow, unknown> }>;
  disableLabelManagement?: boolean;
  scrollToSection?: (sectionId: string, override?: boolean) => void;
  reportMetadata?: CreativeReportMetadata | null;
  showCreativeTypeIcon?: boolean;
  showAdsText?: string;
  showPaginationIfPagination?: boolean;
  paginationTitle?: string;
  hidePageSizeSelector?: boolean; // Hide the page size selector in pagination
  overwritePrimaryCell?: (cell: Cell<CreativeReportRow, unknown>) => React.ReactNode;
  // Metrics functionality
  metricOptions?: ColumnOption[];
  selectedMetrics?: ColumnOption[];
  onMetricChange?: (metrics: ColumnOption[]) => void;
  showAddMetricButton?: boolean;
  groupByKey?: string;
  sectionMetricLabels?: { id: string; label: string }[];
  breakdownValue?: string | null;
  disableGallerySearch?: boolean;
  hideBreakdownRows?: boolean;
  serverPagination?: ServerPaginationState;
  onServerPageChange?: (page: number) => void;
  onServerPageSizeChange?: (pageSize: number) => void;
  isServerSorted?: boolean;
  isPaginationLoading?: boolean;
  /** v2: fetch a page of ads for a group to power gallery infinite scroll */
  fetchGalleryAds?: (
    groupValue: string,
    page: number,
    pageSize: number,
  ) => Promise<AdDetailResponse | null>;
}

const CreativeTable = memo(
  ({
    data,
    columns,
    ad_columns,
    breakdown_columns,
    setGroupSheetOpen,
    setSelectedAdGroup,
    onOpenGroupSheetWithMode,
    setPreviewMediaItem,
    totalRow,
    uniqueLabels,
    status = "success",
    preservePaginationState = true,
    initialPageSize,
    onColumnResize,
    initialColumnSizing,
    onSort,
    initialSortState,
    viewMode,
    sectionId,
    groupType,
    creativeType,
    labelView,
    labelConfigId,
    accountId,
    hideRightPanelTrigger,
    CustomCell,
    disableLabelManagement,
    scrollToSection,
    reportMetadata,
    showCreativeTypeIcon = false,
    showAdsText,
    showPaginationIfPagination,
    paginationTitle,
    hidePageSizeSelector = false,
    overwritePrimaryCell,
    // Metrics functionality
    metricOptions = [],
    selectedMetrics = [],
    onMetricChange,
    showAddMetricButton = false,
    groupByKey,
    sectionMetricLabels,
    breakdownValue,
    disableGallerySearch = false,
    hideBreakdownRows = false,
    serverPagination,
    onServerPageChange,
    onServerPageSizeChange,
    isServerSorted = false,
    isPaginationLoading = false,
    fetchGalleryAds,
  }: CreativeTableProps) => {
    // Search input ref
    const searchInputRef = useRef<HTMLInputElement>(null);
    const shouldFocusSearch = useRef<boolean>(false);
    // Find the breakdown primary column key (the one that's is_primary in breakdown_columns)
    const breakdownPrimaryColumnKey = useMemo(() => {
      if (!breakdown_columns || breakdown_columns.length === 0) return null;
      return breakdown_columns.find((col) => col.is_primary === true)?.key || null;
    }, [breakdown_columns]);

    // Merge breakdown columns with regular columns (only add breakdown columns that aren't already in columns)
    // Exclude the breakdown primary column as it will be shown in the parent primary column
    const mergedColumns = useMemo(() => {
      return columns;
    }, [columns]);

    // Separate rows with primary column including "N/A" into Miscellaneous Ads
    const primaryColumnKey = useMemo(
      () => mergedColumns.find((c) => c.is_primary)?.key,
      [mergedColumns],
    );

    // Keep original data structure - breakdowns will be rendered as visual children
    const processedData = useMemo(() => {
      if (!data || !Array.isArray(data)) return [];
      return data;
    }, [data]);

    const splitData = useMemo(() => {
      if (!primaryColumnKey)
        return { filtered: processedData || [], misc: [] as CreativeReportRow[] };
      const rows = Array.isArray(processedData) ? processedData : [];
      const misc = rows.filter((row) => {
        const v = (row as unknown as Record<string, unknown>)[primaryColumnKey];
        return typeof v === "string" && v.includes("N/A");
      });
      const filtered = rows.filter((row) => {
        const v = (row as unknown as Record<string, unknown>)[primaryColumnKey];
        return !(typeof v === "string" && v.includes("N/A"));
      });
      return { filtered, misc };
    }, [processedData, primaryColumnKey]);

    // Toggle for showing Miscellaneous summary row (default open when there are misc rows)
    const [showMiscRow, setShowMiscRow] = useState<boolean>(
      () => splitData.misc.length > 0,
    );

    // Miscellaneous summary row: use first misc row as-is (no aggregation)
    const miscTotalRow = useMemo(() => {
      if (!splitData.misc || splitData.misc.length === 0) return undefined;
      const row = { ...splitData.misc[0] };
      if (primaryColumnKey) {
        row[primaryColumnKey] = "Miscellaneous Ads";
      }
      return row;
    }, [splitData.misc, primaryColumnKey]);

    const selectedAccount = useGlobalStore((state) => state.selectedAccount);
    const [openGalleryView, setOpenGalleryView] = useState<boolean>(false);
    const [galleryMediaItems, setGalleryMediaItems] = useState<
      (Creative & {
        type: "image" | "video" | "mixed";
        ad_name: string;
        ad_id?: string;
        creative_id?: string;
        videoRetentionData?: VideoRetentionData;
      } & Record<string, unknown>)[]
    >([]);

    // v2 gallery infinite scroll state
    const galleryGroupValueRef = useRef<string>("");
    const galleryNextPageRef = useRef<number>(2);
    const galleryTotalRef = useRef<number>(0);
    const [isGalleryLoading, setIsGalleryLoading] = useState(false);

    const galleryTotalCreatives = fetchGalleryAds ? galleryTotalRef.current : undefined;

    // Helper to compute retention data for a single ad
    const computeRetentionData = useCallback(
      (ad: CreativeReportAd): VideoRetentionData | undefined => {
        if (!ad_columns) return undefined;

        const retentionColumns = ad_columns.filter(
          (col) => col.column_type === "media_data_node" && col.percentage !== undefined,
        );

        if (retentionColumns.length === 0) return undefined;

        const retentionData: VideoRetentionData = [];

        retentionColumns.forEach((col) => {
          const value = ad[col.key];
          if (typeof value === "number" && !isNaN(value)) {
            retentionData.push({
              percentage: col.percentage!,
              retention: value,
            });
          } else if (typeof value === "string" && value !== "" && value !== "N/A") {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              retentionData.push({
                percentage: col.percentage!,
                retention: numValue,
              });
            }
          }
        });

        return retentionData.length > 0
          ? retentionData.sort((a, b) => a.percentage - b.percentage)
          : undefined;
      },
      [ad_columns],
    );

    const handleGalleryLoadMore = useCallback(async () => {
      if (!fetchGalleryAds || !galleryGroupValueRef.current) return;
      const page = galleryNextPageRef.current;
      const response = await fetchGalleryAds(galleryGroupValueRef.current, page, 25);
      if (!response || response.status !== "success" || !response.ads.length) return;

      const winningColumns =
        ad_columns?.filter(
          (col) =>
            (col as CreativeReportColumn & { winning_criteria_name?: string })
              .winning_criteria_name,
        ) || [];

      const newItems = response.ads.map((ad) => {
        const winningCriteriaData: Record<string, boolean> = {};
        winningColumns.forEach((col) => {
          const value = (ad as Record<string, unknown>)[col.key];
          if (typeof value === "boolean") winningCriteriaData[col.key] = value;
        });
        return {
          ...ad.creative,
          type: ad.creative_type,
          ad_name: ad.ad_name || "",
          ad_id: ad.ad_id || "",
          creative_id: ad.creative_id || "",
          videoRetentionData:
            ad.creative_type === "video" ? computeRetentionData(ad) : undefined,
          ...winningCriteriaData,
        };
      });

      setGalleryMediaItems((prev) => [...prev, ...newItems]);
      galleryNextPageRef.current = page + 1;
      if (response.pagination) {
        galleryTotalRef.current = response.pagination.total_ads;
      }
    }, [fetchGalleryAds, ad_columns, computeRetentionData]);
    const [currentGroup, setCurrentGroup] = useState<string | null>(null);
    const isScrolled = useRef<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [expandedHeaders, setExpandedHeaders] = useState<Record<string, boolean>>({});
    const [expandedNodeGroups, setExpandedNodeGroups] = useState<Record<string, boolean>>(
      {},
    );
    const [labelViewByColumn, setLabelViewByColumn] = useState<
      Record<string, "multiple" | "sequence">
    >({});
    const anyExpanded = useMemo(
      () => Object.values(expandedHeaders).some(Boolean),
      [expandedHeaders],
    );
    const anyNodeGroupExpanded = useMemo(
      () => Object.values(expandedNodeGroups).some(Boolean),
      [expandedNodeGroups],
    );
    const hasCompareDate = useMemo(
      () =>
        Boolean(
          reportMetadata?.compare_date?.start_date &&
          reportMetadata?.compare_date?.end_date,
        ),
      [reportMetadata?.compare_date?.start_date, reportMetadata?.compare_date?.end_date],
    );

    // Find the first numeric column for default sorting
    const defaultSortColumn = useMemo(() => {
      const numericColumn = mergedColumns.find(
        (col) =>
          !col.is_primary && (col.column_type === "number" || col.data_type === "number"),
      );
      return numericColumn?.key || null;
    }, [mergedColumns]);

    // Compute the effective initial sort state (use first numeric column if no initial sort)
    const effectiveInitialSortState = useMemo(() => {
      if (initialSortState) {
        return initialSortState;
      }
      if (defaultSortColumn) {
        return { column: defaultSortColumn, direction: "desc" as const };
      }
      return { column: null, direction: false as const };
    }, [initialSortState, defaultSortColumn]);
    // Reset expanded headers when compare date is removed
    useEffect(() => {
      if (!hasCompareDate) {
        setExpandedHeaders({});
      }
    }, [hasCompareDate]);

    // Store sorting state
    const [sortState, setSortState] = useState<{
      column: string | null;
      direction: "asc" | "desc" | false;
    }>(effectiveInitialSortState);

    const handleSortChange = useCallback(
      (newSortState: { column: string | null; direction: "asc" | "desc" | false }) => {
        setSortState(newSortState);
        if (onSort) onSort(newSortState);
      },
      [onSort],
    );

    const pin_cols = columns
      .filter((col) => col.is_primary === true)
      .map((col) => col.key);

    // Update container width on mount and resize
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          const newWidth = containerRef.current.offsetWidth;
          // Only update if width actually changed to prevent unnecessary re-renders
          if (newWidth !== containerWidth) {
            setContainerWidth(newWidth);
          }
        }
      };

      // Set initial width immediately to prevent layout shift
      if (containerRef.current && containerWidth === 0) {
        const initialWidth = containerRef.current.offsetWidth;
        setContainerWidth(initialWidth);
      }

      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [containerWidth]);

    // Find the first primary column
    const firstPrimaryColumn = mergedColumns.find((col) => col.is_primary === true)?.key;

    // Custom sorting function to handle N/A values and text_ai_label arrays
    const customSortingFn = (
      rowA: Row<CreativeReportRow>,
      rowB: Row<CreativeReportRow>,
      columnId: string,
    ) => {
      const aValue = rowA.getValue(columnId);
      const bValue = rowB.getValue(columnId);

      // Helper function to check if a value is N/A
      const isNAValue = (val: unknown) => {
        const str = String(val);
        return str === "N/A" || str === "n/a" || str === "";
      };

      // Helper function to get sortable value for text_ai_label arrays
      const getSortableValue = (val: unknown): unknown => {
        if (Array.isArray(val)) {
          try {
            // For text_ai_label arrays, concatenate labels without separators
            return val.reduce((acc: string, curr) => {
              // Check if current item is an object with a label property
              if (
                curr &&
                typeof curr === "object" &&
                "label" in curr &&
                typeof (curr as { label: string }).label === "string"
              ) {
                return acc + (curr as { label: string }).label;
              }
              // If not a valid object with label, try to convert to string as fallback
              return acc + String(curr || "");
            }, "");
          } catch (error) {
            // If any error occurs during processing, return the original value
            console.warn("Error processing array for sorting:", error);
            return String(val);
          }
        }
        return val;
      };

      const aSortable = getSortableValue(aValue);
      const bSortable = getSortableValue(bValue);

      const aIsNA = isNAValue(aSortable);
      const bIsNA = isNAValue(bSortable);

      // If both are N/A, they are equal
      if (aIsNA && bIsNA) return 0;

      // Get the current sort direction for this column
      const currentSort = sortState.column === columnId ? sortState.direction : false;

      // Handle N/A values based on sort direction
      if (aIsNA && !bIsNA) {
        // A is N/A, B is not - A should go to bottom
        // For ascending, return positive (A comes after B)
        // For descending, return negative (A comes after B when reversed)
        return currentSort === "desc" ? -1 : 1;
      }

      if (!aIsNA && bIsNA) {
        // B is N/A, A is not - B should go to bottom
        // For ascending, return negative (A comes before B)
        // For descending, return positive (A comes before B when reversed)
        return currentSort === "desc" ? 1 : -1;
      }

      // Neither is N/A, perform normal comparison
      const aNum = Number(aSortable);
      const bNum = Number(bSortable);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }

      // String comparison
      return String(aSortable).localeCompare(String(bSortable));
    };

    const table_columns: ColumnDef<CreativeReportRow>[] = [];
    mergedColumns.forEach((col) => {
      const isNumber = isColumnNumber(col);
      const isNodeGroup = isColumnNodeGroup(col);
      const isNode = isColumnNode(col);
      const isFirst = col.key === firstPrimaryColumn;
      const hasSequenceData = labelView === "sequence";

      // Handle node_group columns
      if (isNodeGroup) {
        if (expandedNodeGroups[col.key]) {
          return; // Skip node_group column when expanded (it'll be shown in top row)
        }
        // When collapsed: include node_group column (it will use default_key for data)
      }

      // Handle child node columns based on parent group state
      if (isNode) {
        const parentGroup = columns.find(
          (c) =>
            (c.column_type as string) === "node_group" && c.children?.includes(col.key),
        );
        if (parentGroup) {
          if (!expandedNodeGroups[parentGroup.key]) {
            // When collapsed: skip all children (node_group will show default_key data)
            return;
          }
          // When expanded: include all children
        }
      }
      // Base column
      table_columns.push({
        id: col.key,
        accessorKey:
          isNodeGroup && !expandedNodeGroups[col.key]
            ? col.default_key || col.key
            : col.key,
        sortingFn: customSortingFn,
        size:
          initialColumnSizing && col.key in initialColumnSizing
            ? initialColumnSizing[col.key]
            : getColumnSize(col, isFirst, hasSequenceData, containerWidth),
        enableResizing: true,
        minSize: isFirst ? 285 : 100,
        maxSize: isFirst ? (containerWidth || 1200) * 0.8 : 500,
        meta: {
          align: getColumnAlignment(col, columns),
          label: col.label,
          type: col.column_type,
          dataType: col.default_key
            ? columns.find((c) => c.key === col.default_key)?.data_type
            : col.data_type,
          suffix: col?.suffix ?? "",
          prefix: col?.prefix ?? "",
          info: col?.messages?.info,
          warning: col?.messages?.warning,
          uniqueLabels: col?.unique_labels,
          labelType: col?.label_type,
        } as CreativeTableColumnMeta,
        header: ({ column }: { column: Column<CreativeReportRow, unknown> }) => {
          const currentSort = sortState.column === col.key ? sortState.direction : false;
          return (
            <div className={`flex items-center w-full pl-2 pr-3  min-w-0`}>
              <div className={`flex ${isNumber ? "order-1" : "order-2 ml-auto"}`}>
                {/* Sort button container: visible on hover; always visible if current column is sorted or is primary column; hidden when column is expanded */}
                <div
                  className={cn(
                    "flex transition-opacity mr-1",
                    // Always visible for primary column, or if sorted, or on hover
                    (col.is_primary && col.key === firstPrimaryColumn) || currentSort
                      ? "opacity-100 pointer-events-auto"
                      : "opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto",
                    !isNumber && "mr-0",
                    "[&:has([data-state=open])]:opacity-100 [&:has([data-state=open])]:pointer-events-auto",
                    expandedHeaders[col.key] && "hidden", // Hide when column is expanded
                  )}
                >
                  {/* Label view toggle for text_ai_label with multiple */}
                  {col.column_type === "text_ai_label" &&
                    col.label_type === "multiple" &&
                    col.creative_type === "video" && (
                      <div className="inline-flex mr-1">
                        <SelectDropdown
                          options={[
                            {
                              key: "multiple",
                              label: "Multiple",
                              value: "multiple",
                              icon: RiColorFilterLine,
                              info: "Creatives can be in multiple groups. Great for cross-tagging.",
                            },
                            {
                              key: "sequence",
                              label: "Sequence",
                              value: "sequence",
                              icon: RiStackLine,
                              info: "Groups are defined by exact order. Use for topic-by-topic flows in one video.",
                            },
                          ]}
                          selected={labelViewByColumn[col.key] || "multiple"}
                          onChange={(v) =>
                            setLabelViewByColumn((prev) => ({
                              ...prev,
                              [col.key]: v as "multiple" | "sequence",
                            }))
                          }
                          className=""
                          showOutline={false}
                          customTrigger={
                            <span className="inline-flex">
                              <CommonButton
                                variant="icon"
                                hasOutline
                                bgColor="white"
                                hoverBgColor="#F6F6F6"
                                className={cn("h-7 w-7 shrink-0 p-0")}
                                aria-label="Label View"
                              >
                                <RiEyeLine size={14} color="#9AA3BD" />
                              </CommonButton>
                            </span>
                          }
                          dropdownAlign="start"
                          sortAlphabetically={false}
                        />
                      </div>
                    )}
                  <SortButton
                    columnId={col.key}
                    sortState={sortState}
                    handleSortChange={handleSortChange}
                    column={column}
                    className={cn("h-7 w-7 shrink-0", !isNumber && "mr-0")}
                  />
                </div>

                {/* Expand/collapse button container: only visible on hover; gated by compare-date availability */}
                {shouldShowExpandButton(col, hasCompareDate) && (
                  <div className="opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity">
                    <ExpandCollapseButton
                      isExpanded={expandedHeaders[col.key]}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedHeaders((prev) => ({
                          ...prev,
                          [col.key]: !prev[col.key],
                        }));
                      }}
                    />
                  </div>
                )}

                {/* Expand/collapse button for node_group columns */}
                {shouldShowNodeGroupExpandButton(col) && (
                  <div className="opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity">
                    <ExpandCollapseButton
                      isExpanded={expandedNodeGroups[col.key]}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedNodeGroups((prev) => ({
                          ...prev,
                          [col.key]: !prev[col.key],
                        }));
                      }}
                    />
                  </div>
                )}
              </div>

              <div
                className={`flex gap-1 py-0 hover:text-none overflow-hidden min-w-0 items-center w-full ${
                  getColumnAlignment(col, columns) === "right"
                    ? "order-2 ml-auto justify-end"
                    : "order-1 justify-start"
                } ${
                  col.is_primary && col.key === firstPrimaryColumn
                    ? "justify-between"
                    : ""
                }`}
              >
                <div className="flex items-center gap-[6px] min-w-0">
                  {col?.messages?.warning && (
                    <SqTooltipProvider>
                      <SqTooltip>
                        <SqTooltipTrigger asChild className="cursor-pointer">
                          <IconAlertTriangle
                            size={16}
                            color="#F18400"
                            className="shrink-0"
                          />
                        </SqTooltipTrigger>
                        <SqTooltipContent
                          side="top"
                          align="start"
                          collisionPadding={10}
                          className="max-w-[224px] h-auto text-wrap text-left whitespace-pre-line"
                          portal
                        >
                          {col?.messages?.warning}
                        </SqTooltipContent>
                      </SqTooltip>
                    </SqTooltipProvider>
                  )}
                  {col?.sub_type === "EXTERNAL" && (
                    <IconDatabase size={16} color="#02185999" className="shrink-0" />
                  )}
                  {/* Show creative_type icon for text_ai_label columns */}
                  {col.column_type === "text_ai_label" && showCreativeTypeIcon && (
                    <div
                      className="h-[20px] w-[20px] flex justify-center items-center rounded-[4px] shrink-0"
                      style={{
                        backgroundColor:
                          col.creative_type === "image"
                            ? "rgba(255, 70, 153, 0.08)"
                            : col.creative_type === "video"
                              ? "rgba(1, 140, 98, 0.08)"
                              : "rgba(83, 68, 255, 0.08)",
                      }}
                    >
                      {col.creative_type === "image" ? (
                        <RiImage2Line size={14} color="#FF4699" />
                      ) : col.creative_type === "video" ? (
                        <RiMovieLine size={14} color="#018C62" />
                      ) : (
                        <MultiMediaIcon size={14} />
                      )}
                    </div>
                  )}
                  {/* Show header label only when not searching */}
                  {!(
                    col.is_primary &&
                    col.key === firstPrimaryColumn &&
                    isSearchActive
                  ) && (
                    <SqTooltipProvider>
                      <SqTooltip>
                        <SqTooltipTrigger asChild className="cursor-pointer min-w-0">
                          <span className="truncate font-medium text-[12px] max-w-full block">
                            {col.label}
                            {col.show_platforms && (
                              <span className="text-[#FF4500]">*</span>
                            )}
                          </span>
                        </SqTooltipTrigger>

                        <SqTooltipContent
                          side="top"
                          align="start"
                          className={cn(
                            "squircle-fill-[#18181BF2]",
                            col?.display_formula && "min-w-[240px] p-2",
                            "backdrop-blur-[4px]",
                          )}
                          portal
                        >
                          <div className="flex flex-col gap-1.5">
                            <span>
                              {col.label}
                              <br />
                              {col.show_platforms && col?.platforms
                                ? `Available for ${col?.platforms
                                    .map(
                                      (platform) =>
                                        platform.charAt(0).toUpperCase() +
                                        platform.slice(1).toLowerCase(),
                                    )
                                    .join(", ")}`
                                : ""}
                            </span>
                            {col?.display_formula && (
                              <div className="flex flex-col gap-1.5">
                                <span className="text-[#FAFAFA] font-normal not-italic text-[11px] leading-[20px] tracking-normal">
                                  Formula
                                </span>
                                <p className="squircle squircle-outline-[1px] squircle-outline-color-[#747474] squircle-radius-[4px] px-1.5 pt-0.5 text-[#FF8F66] font-normal text-[11px] leading-[20px] tracking-normal">
                                  {col?.display_formula}
                                </p>
                              </div>
                            )}
                          </div>
                        </SqTooltipContent>
                      </SqTooltip>
                    </SqTooltipProvider>
                  )}
                </div>
                {/* Add search component only for the first primary column */}
                {col.is_primary &&
                  col.key === firstPrimaryColumn &&
                  (isSearchActive ? (
                    <div className="bg-white rounded-[8px] flex items-center gap-2 w-full squircle squircle-radius-[8px] squircle-smooth squircle-outline-[1px] squircle-outline-color-border-gray h-7 mr-1 px-1">
                      <RiSearchLine size={14} color="#9aa3bd" className="ml-1" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={`Search ${col.label.toLowerCase()}`}
                        className="outline-none ring-0 focus:ring-0 focus:outline-none bg-transparent flex-1 py-1 text-[12px] font-normal rounded-md translate-y-[1px] h-6 p-0"
                        key="search-input"
                        autoFocus
                      />
                      <button onClick={handleSearchClose} className="p-1 rounded-md">
                        <RiCloseLine size={14} color="#9aa3bd" />
                      </button>
                    </div>
                  ) : (
                    <CommonButton
                      variant="icon"
                      bgColor="white"
                      hasOutline
                      onClick={handleSearchClick}
                      className={cn(
                        "h-7 w-7 mr-1",
                        // Always visible for primary column
                        col.is_primary && col.key === firstPrimaryColumn
                          ? "opacity-100"
                          : "opacity-0 group-hover/header:opacity-100",
                      )}
                      hoverBgColor="#FBFBFB"
                    >
                      <RiSearchLine size={14} color="#9AA3BD" />
                    </CommonButton>
                  ))}
              </div>
            </div>
          );
        },
        cell: ({ row }: { row: Row<CreativeReportRow> }) => {
          const value = row.getValue(col.key);
          if (value === "") return value as string;
          const numValue = Number(value);

          // Handle node columns (child columns of node_group)
          if (isNumber && !isNaN(numValue)) {
            const isBaseLoading = isCellLoading(col.key, String(value));
            if (isBaseLoading) {
              return (
                <div className="w-full px-2 pt-1">
                  <Skeleton className="h-[23px]" />
                </div>
              );
            }

            // Format the value with suffix (e.g., " / {total_ads} Ads")
            const totalAds = (row.original as Record<string, unknown>).total_ads as
              | number
              | null
              | undefined;
            const formattedValue = formatValueWithSuffix(
              numValue,
              col?.prefix || "",
              col?.suffix || "",
              totalAds,
            );

            // Get percent change for child node columns
            const percent = (row.original as Record<string, unknown>)[
              `${col.key}_percent_change`
            ];
            const isPercentLoading = isCellLoading(
              `${col.key}_percent_change`,
              typeof percent === "string" || typeof percent === "number"
                ? String(percent)
                : "",
            );

            return (
              <RenderToolTippedCell
                cellText={
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-[12px]">{formattedValue}</div>
                    <PercentageText
                      value={percent as number | string | null | undefined}
                      isLoading={isPercentLoading}
                    />
                  </div>
                }
                headerText={col.label || ""}
                align={col.data_type === "number" ? "right" : "left"}
                isPinned={false}
                columnKey={col.key}
                info={col?.messages?.info}
              />
            );
          }

          // Handle node_group columns when collapsed
          if (col.column_type === "node_group" && !expandedNodeGroups[col.key]) {
            // When collapsed, show the default_key column's value
            const defaultKey = col.default_key;
            if (defaultKey) {
              const childValue = (row.original as Record<string, unknown>)[defaultKey];
              const childColumn = columns.find((c) => c.key === defaultKey);

              if (childColumn && childColumn.data_type === "number") {
                const numValue = Number(childValue);
                if (!isNaN(numValue)) {
                  const totalAds = (row.original as Record<string, unknown>).total_ads as
                    | number
                    | null
                    | undefined;
                  const formattedValue = formatValueWithSuffix(
                    numValue,
                    childColumn?.prefix || "",
                    childColumn?.suffix || "",
                    totalAds,
                  );

                  // Get percent change for the default key column
                  const percent = (row.original as Record<string, unknown>)[
                    `${defaultKey}_percent_change`
                  ];
                  const isPercentLoading = isCellLoading(
                    `${defaultKey}_percent_change`,
                    typeof percent === "string" || typeof percent === "number"
                      ? String(percent)
                      : "",
                  );

                  const alignment =
                    childColumn.data_type === "number" ? "text-right" : "text-left";

                  return (
                    <RenderToolTippedCell
                      cellText={
                        <div className="flex flex-col items-end gap-1">
                          <div className={`text-[12px] ${alignment}`}>
                            {formattedValue}
                          </div>
                          <PercentageText
                            value={percent as number | string | null | undefined}
                            isLoading={isPercentLoading}
                          />
                        </div>
                      }
                      headerText={col.label || ""}
                      align={childColumn.data_type === "number" ? "right" : "left"}
                      isPinned={false}
                      columnKey={col.key}
                      info={col?.messages?.info}
                    />
                  );
                }
              }

              return (
                <div className="text-[12px] text-left">
                  {JSON.stringify(childValue || "—")}
                </div>
              );
            }

            return (
              <div className="text-[12px] text-left text-brand-primary-new/60">
                — {/* Placeholder for collapsed group */}
              </div>
            );
          }

          return String(value);
        },
      });

      // Expanded extra columns for child node columns with number data_type
      if (isNumber && expandedHeaders[col.key]) {
        table_columns.push({
          id: `${col.key}_previous`,
          accessorFn: (r) =>
            (r as unknown as Record<string, unknown>)[`${col.key}_previous`],
          sortingFn: customSortingFn,
          size: 147,
          enableResizing: true,
          minSize: 100,
          maxSize: 500,
          meta: {
            align: "right",
            label: "Previous",
            type: "number",
            suffix: col?.suffix ?? "",
            prefix: col?.prefix ?? "",
          } as CreativeTableColumnMeta,
          header: () =>
            expandedHeaders[col.key] ? null : (
              <span className="truncate font-medium text-[12px]">Previous</span>
            ),
          cell: ({ row }) => {
            const v = (row.original as Record<string, unknown>)[`${col.key}_previous`];
            const isLoading = isCellLoading(
              `${col.key}_previous`,
              typeof v === "string" || typeof v === "number" ? String(v) : "",
            );
            if (isLoading)
              return (
                <div className="w-full px-2 pt-1">
                  <Skeleton className="h-[23px]" />
                </div>
              );
            const num = Number(v);
            if (isNaN(num)) {
              return typeof v === "string" ? v : "";
            }

            const totalAds = (row.original as Record<string, unknown>)
              .total_ads_previous as number | null | undefined;
            const formattedValue = formatValueWithSuffix(
              num,
              col.prefix || "",
              col.suffix || "",
              totalAds,
            );

            return formattedValue;
          },
        });

        table_columns.push({
          id: `${col.key}_change`,
          accessorFn: (r) =>
            (r as unknown as Record<string, unknown>)[`${col.key}_change`],
          sortingFn: customSortingFn,
          size: 147,
          enableResizing: true,
          minSize: 100,
          maxSize: 500,
          meta: {
            align: "right",
            label: "Change",
            type: "number",
            suffix: col?.suffix ?? "",
            prefix: col?.prefix ?? "",
          } as CreativeTableColumnMeta,
          header: () =>
            expandedHeaders[col.key] ? null : (
              <span className="truncate font-medium text-[12px]">Change</span>
            ),
          cell: ({ row }) => {
            const v = (row.original as Record<string, unknown>)[`${col.key}_change`];
            const isLoading = isCellLoading(
              `${col.key}_change`,
              typeof v === "string" || typeof v === "number" ? String(v) : "",
            );
            if (isLoading)
              return (
                <div className="w-full px-2 pt-1">
                  <Skeleton className="h-[23px]" />
                </div>
              );
            const num = Number(v);
            if (isNaN(num)) {
              return typeof v === "string" ? v : "";
            }

            const totalAds = (row.original as Record<string, unknown>)
              .total_ads_change as number | null | undefined;
            const formattedValue = formatValueWithSuffix(
              num,
              col.prefix || "",
              col.suffix || "",
              totalAds,
              true, // isChangeColumn
            );

            return formattedValue;
          },
        });
      }
    });
    const [sorting, setSorting] = React.useState<SortingState>(
      effectiveInitialSortState && effectiveInitialSortState.column
        ? [
            {
              id: effectiveInitialSortState.column,
              desc: effectiveInitialSortState.direction === "desc",
            },
          ]
        : [],
    );
    const isUserInitiatedPaginationChanges = useRef<boolean>(false);

    // User's selected page size (represents parent rows only)
    const initialUserPageSize =
      initialPageSize || (viewMode === "line_chart_view" ? 6 : 10);

    // Track the user's selected page size (what they see in the UI)
    const [userPageSize, setUserPageSize] = useState(initialUserPageSize);

    // Page size is now just the user page size since breakdowns are visual children
    const [pagination, setPagination] = React.useState({
      pageIndex: 0,
      pageSize: userPageSize,
    });

    const [columnSizing, setColumnSizing] = useState<Record<string, number>>(
      initialColumnSizing || {},
    );

    // Simple search logic - single search for first primary column
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isSearchActive, setIsSearchActive] = useState<boolean>(false);

    // Search functions
    const getSearchableValue = (row: CreativeReportRow, columnKey: string): string => {
      const value = row[columnKey as keyof CreativeReportRow];
      return value ? String(value as string) : "";
    };

    const isValueMatchingQuery = (value: string, query: string): boolean => {
      return value.toLowerCase().includes(query.toLowerCase());
    };

    // Search handlers
    const handleSearchClick = useCallback(() => {
      setIsSearchActive(true);
      shouldFocusSearch.current = true;
    }, []);

    const handleSearchClose = useCallback(() => {
      setIsSearchActive(false);
      setSearchQuery("");
    }, []);

    const handleSearchKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
          handleSearchClose();
        }
      },
      [handleSearchClose],
    );

    const handleSearchInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      [],
    );

    // Focus search input only when first activated (not on re-renders)
    useEffect(() => {
      if (isSearchActive && shouldFocusSearch.current && searchInputRef.current) {
        searchInputRef.current.focus();
        shouldFocusSearch.current = false; // Reset the flag
      }
    }, [isSearchActive]);

    // Filter data based on search query using useMemo to reduce re-renders
    const filteredData = useMemo(() => {
      if (!splitData.filtered) return [];

      if (!searchQuery.trim() || !isSearchActive) {
        return splitData.filtered;
      }

      // Only return matching rows when search is active
      return splitData.filtered.filter((row) => {
        const searchableValue = getSearchableValue(row, firstPrimaryColumn || "");
        return isValueMatchingQuery(searchableValue, searchQuery);
      });
    }, [splitData.filtered, searchQuery, isSearchActive, firstPrimaryColumn]);

    // Handler for pagination changes - breakdowns are visual children, so no adjustment needed
    const handlePaginationChange = useCallback(
      (updater: typeof pagination | ((prev: typeof pagination) => typeof pagination)) => {
        setPagination((prev) => {
          const newPagination = typeof updater === "function" ? updater(prev) : updater;

          // If page size changed, update user page size state
          if (newPagination.pageSize !== prev.pageSize) {
            setUserPageSize(newPagination.pageSize);
            onServerPageSizeChange?.(newPagination.pageSize);
          } else if (newPagination.pageIndex !== prev.pageIndex) {
            // page is 1-indexed on the server
            onServerPageChange?.(newPagination.pageIndex + 1);
          }

          return newPagination;
        });
      },
      [onServerPageChange, onServerPageSizeChange],
    );

    const table = useReactTable({
      data: filteredData || [],
      columns: [
        ...table_columns,
        ...(table_columns.reduce((total, col) => total + col.size!, 0) <
          (containerWidth || 1200) && !showAddMetricButton
          ? [
              {
                id: SPACER,
                accessorKey: SPACER,
                meta: {
                  align: "left",
                  label: "",
                  type: SPACER,
                } as CreativeTableColumnMeta,
                maxSize: Number.MAX_SAFE_INTEGER,
                minSize: 0,
                enableResizing: false,
                header: () => null,
                cell: () => null,
                enableHiding: true,
              },
            ]
          : []),
      ],
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      ...(isServerSorted ? {} : { getSortedRowModel: getSortedRowModel() }),
      manualSorting: isServerSorted,
      manualPagination: !!serverPagination,
      ...(serverPagination ? { pageCount: serverPagination.totalPages } : {}),
      onPaginationChange: handlePaginationChange,
      columnResizeMode: "onChange" as const,
      enableColumnResizing: true,
      columnResizeDirection: "ltr",
      defaultColumn: {
        size: undefined,
      },
      state: {
        sorting,
        pagination: serverPagination
          ? { pageIndex: serverPagination.page - 1, pageSize: serverPagination.pageSize }
          : pagination,
        columnSizing,
        columnPinning: { left: pin_cols },
      },
      enableColumnPinning: true,
      initialState: {
        columnPinning: {
          left: pin_cols,
        },
        pagination: {
          pageSize: serverPagination?.pageSize ?? userPageSize,
        },
      },
      autoResetPageIndex: !preservePaginationState && !serverPagination,
      onColumnSizingChange: setColumnSizing,
    });

    // removed unused stacked-cell helper (was used in per-cell expand)

    const getPinnedColumnStyle = <TData,>(
      column: Column<TData, unknown>,
      isHeader: boolean = false,
    ): CSSProperties => {
      const isPinned = column?.getIsPinned();
      const columnSize = column.getSize();

      // Calculate left position based on sum of previous pinned columns' widths
      const leftOffset = isPinned
        ? table
            .getAllFlatColumns()
            .slice(0, column.getIndex())
            .reduce((sum, column) => sum + column.getSize(), 0)
        : 0;

      return {
        position: isPinned ? "sticky" : "relative",
        zIndex: isPinned ? (isHeader ? 2 : 1) : 0, // Higher z-index for pinned header/cell
        ...(isPinned
          ? {
              left: `${leftOffset}px`,
              width: `${columnSize}px`,
              minWidth: `${columnSize}px`,
              backgroundColor: isHeader ? "#ffffff" : undefined,
            }
          : {
              width: `${columnSize}px`,
              minWidth: `${columnSize}px`,
            }),
      };
    };

    // Helper function to render a sub-header cell with sorting button
    const renderSubHeaderCell = (
      column: Column<CreativeReportRow, unknown>,
      columnId: string,
      label: string,
      isFirst: boolean = false,
      showSortButton: boolean = true,
    ) => (
      <TableHead
        key={`sub-${column.id}`}
        style={{
          ...getPinnedColumnStyle(column, true),
          borderBottom: "1px solid #E4E4E7",
          position: column.getIsPinned() ? "sticky" : "relative",
          ...(isFirst && { borderLeft: "none" }),
        }}
        className={`p-0 h-9 border-r border-[#E4E4E7] last:border-r-0 ${column.getIsLastColumn("left") ? "pinned-last" : ""}
      ${column.getIsPinned() ? "pinned-cell" : ""}`}
      >
        <div className="group px-2 text-[12px] text-brand-primary-new/60 leading-[16px] flex items-center justify-between gap-2 h-full">
          {/* Sorting button on the left - only show if showSortButton is true */}
          {showSortButton && (
            <SortButton
              columnId={columnId}
              sortState={sortState}
              handleSortChange={handleSortChange}
              column={column}
              className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )}
          <div className="flex-1 text-right truncate">{label}</div>
        </div>
      </TableHead>
    );

    const isCellLoading = (columnKey: string, value: string) => {
      if (columnKey === SPACER) return false;
      const v = String(value || "").toUpperCase();
      // Fallback to partial-state checks
      if (status === "partial") {
        return v === "__LOADING" || v === "__LOADING__" || v === "UNDEFINED";
      }
      return false;
    };

    // Background highlight for main metric column based on percent change thresholds
    const getMainColumnBgColor = (columnId: string, row: CreativeReportRow) => {
      if (columnId.endsWith("_previous") || columnId.endsWith("_change"))
        return undefined;
      const rawPercent = (row as unknown as Record<string, unknown>)[
        `${columnId}_percent_change`
      ];
      const numeric = Number(rawPercent);
      if (isNaN(numeric)) return undefined;
      // Use raw percent; highlight only for positive values
      if (numeric <= 0) return undefined;
      if (numeric <= 20) return "#009B4D08";
      if (numeric <= 50) return "#009B4D0F";
      if (numeric <= 100) return "#009B4D1A";
      return "#009B4D29";
    };

    const RenderTextAiDescription = ({
      cellText,
      headerText,
      align,
      columnKey,
    }: {
      cellText: React.ReactNode;
      headerText: string;
      align: string;
      columnKey: string;
    }) => {
      const value =
        React.isValidElement(cellText) && "getValue" in cellText.props
          ? String((cellText.props as { getValue: () => unknown }).getValue())
          : typeof cellText === "string"
            ? cellText
            : "";

      const isLoading = isCellLoading(columnKey, value);
      if (isLoading) {
        return (
          <div className="w-full px-2 pt-1">
            <Skeleton className="h-[23px]" />
          </div>
        );
      }
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="w-full">
              <div
                className={cn(
                  "text-[12px] whitespace-normal break-words leading-[16px]",
                  align === "left" && "line-clamp-3",
                )}
                style={{ textAlign: align as "left" | "center" | "right" }}
              >
                {value === "N/A" ? "—" : cellText}
              </div>
            </TooltipTrigger>
            <Portal>
              <TooltipContent
                side="bottom"
                align="start"
                className={`flex flex-col gap-1 text-[12px] text-left z-[50] bg-white shadow-sm border border-border-gray min-w-[400px] p-3 max-w-[400px] max-h-[300px] overflow-y-auto`}
                avoidCollisions
                alignOffset={25}
                collisionPadding={10}
              >
                <div className="text-brand-primary-800 text-[14px] font-medium leading-[24px] tracking-[0.02em]">
                  {headerText}
                </div>
                <MarkdownFormatter
                  content={value}
                  className="text-brand-primary-new/80 text-[12px] font-normal leading-[22px] tracking-[0.02em]"
                />
              </TooltipContent>
            </Portal>
          </Tooltip>
        </TooltipProvider>
      );
    };

    const RenderTextAiLabel = ({
      cellText,
      align,
      isPinned,
      columnKey,
      info,
      viewMode,
      uniqueLabels,
      labelType,
    }: {
      cellText: React.ReactNode;
      align: string;
      isPinned: string | boolean;
      columnKey: string;
      info?: string | null;
      viewMode?: "multiple" | "sequence";
      uniqueLabels?: string[];
      labelType?: "unique" | "multiple";
    }) => {
      // Extract the underlying value from the flexRender element if present
      let raw: unknown = cellText;
      if (
        React.isValidElement(cellText) &&
        cellText.props &&
        typeof cellText.props === "object"
      ) {
        const props = cellText.props as {
          getValue?: () => unknown;
          cell?: { getValue?: () => unknown };
        };
        if (typeof props.getValue === "function") {
          raw = props.getValue();
        } else if (props.cell && typeof props.cell.getValue === "function") {
          raw = props.cell.getValue();
        }
      }

      const previewText = Array.isArray(raw)
        ? (raw as Array<{ label: string; order: number | null }>).reduce(
            (acc, curr) => (acc.length > 0 ? acc + ", " + curr.label : curr.label),
            "",
          )
        : typeof raw === "string"
          ? raw
          : typeof raw === "object" && raw !== null
            ? ""
            : String(raw);

      const isLoading = isCellLoading(columnKey, previewText);
      if (isLoading) {
        return (
          <div className="w-full px-2 pt-1">
            <Skeleton className="h-[23px]" />
          </div>
        );
      }

      if (Array.isArray(raw) && labelType === "multiple") {
        return (
          <ScrollableFadeContainer
            sequenceData={raw as Array<{ label: string; order: number | null }>}
            uniqueLabels={uniqueLabels}
            viewMode={viewMode}
          />
        );
      }

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="w-full h-full">
              <div
                className={cn(
                  `text-[12px] whitespace-normal break-words ${align === "left" && "line-clamp-3"} text-${align}`,
                )}
              >
                {previewText === "N/A" ? "—" : previewText}
              </div>
            </TooltipTrigger>
            <Portal>
              <TooltipContent
                side="bottom"
                align="start"
                className={`text-[12px] max-w-[340px] text-left z-[50] ${
                  isPinned
                    ? "bg-white shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),_0px_1px_3px_0px_rgba(0,0,0,0.10)] border border-border-gray"
                    : ""
                }`}
                avoidCollisions
                collisionPadding={10}
              >
                <div className="">{info ?? previewText}</div>
              </TooltipContent>
            </Portal>
          </Tooltip>
        </TooltipProvider>
      );
    };

    const RenderToolTippedCell = ({
      cellText,
      headerText,
      align,
      isPinned,
      columnKey,
      info,
      className,
      additionalInfo,
      prefix,
    }: {
      cellText: React.ReactNode;
      headerText: string;
      align: string;
      isPinned: string | boolean;
      columnKey: string;
      info?: string | null;
      className?: string;
      additionalInfo?: string;
      prefix?: string;
    }) => {
      const value =
        React.isValidElement(cellText) && "getValue" in cellText.props
          ? String((cellText.props as { getValue: () => unknown }).getValue())
          : typeof cellText === "string"
            ? cellText
            : "";

      const isLoading = isCellLoading(columnKey, value);
      if (isLoading) {
        return (
          <div className="w-full px-2 pt-1">
            <Skeleton className="h-[23px]" />
          </div>
        );
      }
      const showPlatformIcon = prefix === "{PLATFORM_ICON}";
      const accountPlatform = selectedAccount?.child_accounts?.find(
        (a) => a.name.trim() === value?.trim(),
      )?.platform;
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="w-full h-full">
              <div
                className={cn(
                  `text-[12px] whitespace-normal break-words w-full flex items-center gap-1
              ${align === "left" && "line-clamp-3"}
              text-${align}`,
                  className,
                )}
              >
                {showPlatformIcon && accountPlatform && (
                  <img
                    className="h-[14px] w-[14px] flex-shrink-0 inline-block mr-2"
                    src={getPlatformIcon(accountPlatform)}
                  />
                )}
                <span className="mt-[2px] flex-1">
                  {value === "N/A" ? "—" : cellText}
                </span>
                {additionalInfo && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <RiInformationLine size={14} className="text-brand-primary-400" />
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="start"
                        className="max-w-[250px]"
                      >
                        <p className="text-[12px] text-white font-normal">
                          {additionalInfo}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </TooltipTrigger>
            <Portal>
              <TooltipContent
                hidden={cellText === ""}
                side="bottom"
                align="end"
                className={`text-[12px] max-w-[340px] text-left z-[50] ${
                  isPinned
                    ? "bg-white shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),_0px_1px_3px_0px_rgba(0,0,0,0.10)] border border-border-gray"
                    : ""
                }`}
                avoidCollisions
                collisionPadding={10}
              >
                {isPinned ? (
                  <div className="bg-white text-black p-2">
                    <div className="mb-1 text-brand-primary-new font-[500]">
                      {headerText}
                    </div>
                    {isLoading ? (
                      <div className="flex items-center justify-center text-brand-primary-new">
                        <LuLoaderCircle className="w-4 h-4 animate-spin text-brand-primary-new" />
                      </div>
                    ) : (
                      <p className="whitespace-normal break-words text-brand-primary-new">
                        {info ?? cellText}
                      </p>
                    )}
                  </div>
                ) : isLoading ? (
                  <div className="flex items-center justify-center">
                    <LuLoaderCircle className="w-4 h-4 animate-spin text-brand-primary-new" />
                  </div>
                ) : (
                  <p className="whitespace-normal break-words text-white [&_*]:!text-white">
                    {info ?? cellText}
                  </p>
                )}
              </TooltipContent>
            </Portal>
          </Tooltip>
        </TooltipProvider>
      );
    };
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const scrollLeft = e.currentTarget.scrollLeft;
      const wasScrolled = isScrolled.current;
      isScrolled.current = scrollLeft > 0;

      if (wasScrolled !== isScrolled.current) {
        const pinnedCells = e.currentTarget.querySelectorAll(".pinned-last");
        pinnedCells.forEach((cell) => {
          cell.classList.toggle("pinned-shadow", isScrolled.current);
        });
      }
    };

    const debouncedUpdateSpacer = useCallback(
      (
        table: TableType<CreativeReportRow>,
        containerWidth: number,
        columnSizing: Record<string, number>,
      ) => {
        const debouncedFn = debounce(() => {
          const totalWidth = table
            .getAllColumns()
            .filter((column) => column.id !== SPACER)
            .reduce((total, column) => total + column.getSize(), 0);
          if (
            onColumnResize &&
            columnSizing &&
            Object.keys(columnSizing).length > 0 &&
            columnSizing !== initialColumnSizing
          ) {
            onColumnResize(columnSizing);
          }
          const spacerColumn = table.getColumn(SPACER);
          if (spacerColumn) {
            spacerColumn.toggleVisibility(totalWidth < (containerWidth || 1200));
          }
        }, 150);
        return debouncedFn;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    useEffect(() => {
      const debouncedFn = debouncedUpdateSpacer(table, containerWidth, columnSizing);
      debouncedFn();
      return () => {
        debouncedFn.cancel();
      };
    }, [containerWidth, table, columnSizing, debouncedUpdateSpacer]);

    // Update userPageSize when viewMode or initialPageSize changes
    useEffect(() => {
      const newUserPageSize =
        initialPageSize || (viewMode === "line_chart_view" ? 6 : 10);
      if (userPageSize !== newUserPageSize) {
        setUserPageSize(newUserPageSize);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, initialPageSize]);

    // Add useEffect to update pagination when viewMode or userPageSize changes
    // Skip for v2 (serverPagination) — the table is in controlled mode via state.pagination
    useEffect(() => {
      if (serverPagination) return;
      if (table.getState().pagination.pageSize !== userPageSize) {
        table.setPageSize(userPageSize);
        table.setPageIndex(0);
      }
    }, [viewMode, table, userPageSize, serverPagination]);

    const setVisibleAdNamesBySection = useGlobalStore(
      (state) => state.setVisibleAdNamesBySection,
    );

    // Get all primary column keys from columns prop
    const primaryColumnKeys = useMemo(
      () => columns.filter((col) => col.is_primary).map((col) => col.key),
      [columns],
    );

    const visibleRows = table.getPaginationRowModel().rows;
    // Create all ad names array for the current visible page (for rendering)
    const allAdNames = useMemo(() => {
      return visibleRows.map((row) => {
        const primaryValues = primaryColumnKeys.map((key) => String(row.original[key]));
        return primaryValues.join(PRIMARY_COLUMN_JOINER);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleRows, primaryColumnKeys, pagination]);

    // Get color assignment function for visible ads
    const assignColorsToVisibleAds = useGlobalStore(
      (state) => state.assignColorsToVisibleAds,
    );

    // Update visible ad names and assign colors whenever pagination changes
    // Colors are assigned based on position in the current page to ensure distinct colors
    useEffect(() => {
      if (!sectionId) return;
      // Get only the paginated rows
      const visibleRows = table.getPaginationRowModel().rows;

      const visibleAdNames = visibleRows.map((row) => {
        // Get all primary column values
        const primaryValues = primaryColumnKeys.map((key) => String(row.original[key]));

        return {
          fullName: primaryValues.join(PRIMARY_COLUMN_JOINER),
          sections: primaryValues,
        };
      });

      setVisibleAdNamesBySection(sectionId, visibleAdNames);

      // Assign distinct colors to visible ads on current page
      // This ensures no two ads on the same page share the same color
      const visibleFullNames = visibleAdNames.map((ad) => ad.fullName);
      assignColorsToVisibleAds(sectionId, visibleFullNames);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      table,
      data,
      setVisibleAdNamesBySection,
      assignColorsToVisibleAds,
      pagination,
      sorting,
      sectionId,
    ]);

    useEffect(() => {
      if (isUserInitiatedPaginationChanges.current && sectionId) {
        scrollToSection?.(sectionId, true);
        isUserInitiatedPaginationChanges.current = false;
      }
    }, [pagination.pageIndex, pagination.pageSize, scrollToSection, sectionId]);

    // Column drag-and-drop reordering
    const [activeDragId, setActiveDragId] = useState<string | null>(null);
    const [activeDragWidth, setActiveDragWidth] = useState<number>(0);

    const columnDragSensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 8 },
      }),
    );

    const sortableColumnIds = useMemo(
      () => (selectedMetrics || []).map((m) => m.key),
      [selectedMetrics],
    );

    const handleColumnDragStart = useCallback((event: DragStartEvent) => {
      const id = event.active.id as string;
      setActiveDragId(id);
      const node = event.active.rect.current.initial;
      if (node) setActiveDragWidth(node.width);
    }, []);

    const handleColumnDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragId(null);
        if (!over || active.id === over.id || !onMetricChange || !selectedMetrics) return;

        const oldIndex = selectedMetrics.findIndex((m) => m.key === active.id);
        const newIndex = selectedMetrics.findIndex((m) => m.key === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = [...selectedMetrics];
        const [removed] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, removed);
        onMetricChange(reordered);
      },
      [selectedMetrics, onMetricChange],
    );

    const activeDragColumn = useMemo(() => {
      if (!activeDragId) return null;
      return mergedColumns.find((col) => col.key === activeDragId) || null;
    }, [activeDragId, mergedColumns]);

    return (
      <DndContext
        sensors={columnDragSensors}
        collisionDetection={closestCenter}
        onDragStart={handleColumnDragStart}
        onDragEnd={handleColumnDragEnd}
        modifiers={[restrictToHorizontalAxis]}
      >
        <div ref={containerRef}>
          <style>
            {`
        .pinned-shadow {
          box-shadow: 8px 0 8px -4px rgba(228, 228, 231, 0.40);
        }
        .pinned-last {
          border-right: 1px solid #e5e7eb;
        }
        .pinned-cell {
          background-color: #ffffff;
        }
        tr:hover .pinned-cell {
          background-color: rgb(251,251,251);
        }
        .resizer {
          position: absolute;
          right: 0;
          top: 0;
          height: 100%;
          width: 4px;
          background: rgba(0, 0, 0, 0.1);
          cursor: col-resize;
          user-select: none;
          touch-action: none;
          opacity: 0;
          transition: opacity 0.2s;
        }
        
        .resizer:hover {
          opacity: 1;
        }
        
        .resizer.isResizing {
          opacity: 1;
        }

        .table-container {
          width: 100%;
          overflow-x: auto;
        }
        table {
          table-layout: fixed !important;
        }
        `}
          </style>
          <div
            className="border border-border-gray rounded-[12px] overflow-hidden table-container relative"
            onScroll={handleScroll}
          >
            <Table
              className="border-separate border-spacing-0 cursor-default bg-white"
              style={{
                width: "max-content",
                minWidth: "100%",
              }}
            >
              <TableHeader>
                {/* Node group header row - shows parent group names when expanded (TOP ROW) */}
                {anyNodeGroupExpanded && (
                  <TableRow className="group/row hover:bg-[rgb(251,251,251)] h-9">
                    {(() => {
                      const headers = table.getHeaderGroups()[0]?.headers || [];
                      let isFirstVisibleColumn = true;
                      let skipCount = 0; // Track how many headers to skip due to colSpan

                      return headers.map((header) => {
                        // Skip headers that are covered by previous colSpan
                        if (skipCount > 0) {
                          skipCount--;
                          return null;
                        }

                        const colId = header.id;

                        // Find the parent node group for this column
                        const parentNodeGroup = columns.find(
                          (c) =>
                            (c.column_type as string) === "node_group" &&
                            c.children?.includes(colId),
                        );

                        if (parentNodeGroup && expandedNodeGroups[parentNodeGroup.key]) {
                          // This is a child column of an expanded node group
                          // Check if this is the first child column to render the group header
                          const childColumns = parentNodeGroup.children || [];
                          const isFirstChild = childColumns[0] === colId;

                          if (isFirstChild) {
                            // Calculate actual visible child count including expanded compare columns
                            let visibleChildCount = 0;
                            childColumns.forEach((childKey: string) => {
                              visibleChildCount += 1; // Main child column
                              if (expandedHeaders[childKey]) {
                                visibleChildCount += 2; // Add _previous and _change columns
                              }
                            });

                            const shouldRemoveLeftBorder = isFirstVisibleColumn;
                            isFirstVisibleColumn = false; // Mark that we've found the first visible column

                            // Set skip count to skip the remaining headers covered by this colSpan
                            skipCount = visibleChildCount - 1;

                            return (
                              <TableHead
                                key={`node-group-${parentNodeGroup.key}`}
                                colSpan={visibleChildCount}
                                style={{
                                  ...getPinnedColumnStyle(header.column, true),
                                  position: header.column.getIsPinned()
                                    ? "sticky"
                                    : "relative",
                                  ...(shouldRemoveLeftBorder && { borderLeft: "none" }),
                                }}
                                className={`p-0 h-9 text-brand-primary-new/80 group-hover/row:text-brand-primary-new group/header border-r border-[#E4E4E7] border-b ${header.column.getIsLastColumn("left") ? "pinned-last" : ""}
                              ${header.column.getIsPinned() ? "pinned-cell" : ""}`}
                              >
                                <div
                                  className={`flex items-center w-full pl-2 pr-3 min-w-0`}
                                >
                                  <div
                                    className={`flex ${getColumnAlignment(parentNodeGroup, columns) === "right" ? "order-1" : "order-2 ml-auto"}`}
                                  >
                                    {/* Collapse button for expanded node_group */}
                                    <div className="opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity">
                                      <ExpandCollapseButton
                                        isExpanded={true}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedNodeGroups((prev) => ({
                                            ...prev,
                                            [parentNodeGroup.key]: false,
                                          }));

                                          // Collapse all child nodes when parent is collapsed
                                          const childColumns =
                                            parentNodeGroup.children || [];
                                          const childExpandedUpdates: Record<
                                            string,
                                            boolean
                                          > = {};
                                          childColumns.forEach((childKey: string) => {
                                            childExpandedUpdates[childKey] = false;
                                          });
                                          setExpandedHeaders((prev) => ({
                                            ...prev,
                                            ...childExpandedUpdates,
                                          }));
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div
                                    className={`flex gap-1 py-0 hover:text-none overflow-hidden min-w-0 items-center w-full ${
                                      getColumnAlignment(parentNodeGroup, columns) ===
                                      "right"
                                        ? "order-2 ml-auto justify-end"
                                        : "order-1 justify-start"
                                    }`}
                                  >
                                    <div className="flex items-center gap-[6px] min-w-0">
                                      <div className="truncate font-medium text-[12px] leading-[16px]">
                                        {parentNodeGroup.label}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </TableHead>
                            );
                          } else {
                            // This is not the first child, skip rendering (covered by colSpan)
                            return null;
                          }
                        } else {
                          // This is not a child of an expanded node group, show empty cell
                          const shouldRemoveLeftBorder = isFirstVisibleColumn;
                          isFirstVisibleColumn = false; // Mark that we've found the first visible column

                          return (
                            <TableHead
                              key={`top-${colId}`}
                              colSpan={1}
                              style={{
                                ...getPinnedColumnStyle(header.column, true),
                                borderBottom: "1px solid #E4E4E7",
                                position: header.column.getIsPinned()
                                  ? "sticky"
                                  : "relative",
                                ...(shouldRemoveLeftBorder && { borderLeft: "none" }),
                              }}
                              className={`p-0 h-9 border-r border-[#E4E4E7] last:border-r-0 ${header.column.getIsLastColumn("left") ? "pinned-last" : ""}
                            ${header.column.getIsPinned() ? "pinned-cell" : ""}`}
                            >
                              <div className="px-2 py-1 text-[12px] text-brand-primary-new/60 leading-[16px]"></div>
                            </TableHead>
                          );
                        }
                      });
                    })()}
                    {showAddMetricButton && <TableHead className="border-b"> </TableHead>}
                  </TableRow>
                )}
                {/* Main header row */}
                <SortableContext
                  items={sortableColumnIds}
                  strategy={horizontalListSortingStrategy}
                >
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      key={headerGroup.id}
                      className="group/row hover:bg-[rgb(251,251,251)] h-9"
                    >
                      {headerGroup.headers.map((header, index) => {
                        const isFirstRow = true;
                        const columnId = header.column.id;
                        const baseId = columnId.replace(/(_previous|_change)$/, "");
                        const isSecondary =
                          columnId.endsWith("_previous") || columnId.endsWith("_change");
                        const isExpanded = expandedHeaders[baseId];

                        // Hide node_group columns when they are expanded (they'll be shown in the top row)
                        const meta = header.column.columnDef
                          .meta as CreativeTableColumnMeta;
                        const isExpandedNodeGroup =
                          meta.type === "node_group" && expandedNodeGroups[columnId];
                        if (isExpandedNodeGroup) {
                          return null;
                        }

                        // For collapsed node_group columns, show the parent group name
                        const isCollapsedNodeGroup =
                          meta.type === "node_group" && !expandedNodeGroups[columnId];
                        if (isCollapsedNodeGroup) {
                          // Show the parent group name instead of the first child column name
                          const originalColumn = columns.find((c) => c.key === columnId);
                          if (originalColumn) {
                            return (
                              <TableHead
                                key={header.id}
                                colSpan={1}
                                style={{
                                  ...getPinnedColumnStyle(header.column, true),
                                  position: header.column.getIsPinned()
                                    ? "sticky"
                                    : "relative",
                                  ...(index === 0 && { borderLeft: "none" }),
                                  ...(isFirstRow && { borderTop: "none" }),
                                }}
                                className={`p-0 h-9 text-brand-primary-new/80 group-hover/row:text-brand-primary-new group/header border-r border-[#E4E4E7] border-b ${header.column.getIsLastColumn("left") ? "pinned-last" : ""}
                            ${header.column.getIsPinned() ? "pinned-cell" : ""}`}
                              >
                                <div
                                  className={`flex items-center w-full pl-2 pr-3 min-w-0`}
                                >
                                  <div
                                    className={`flex ${getColumnAlignment(originalColumn, columns) === "right" ? "order-1" : "order-2 ml-auto"}`}
                                  >
                                    {/* Sort button container for collapsed node_group columns */}
                                    <div
                                      className={cn(
                                        "flex opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity mr-1",
                                        !getColumnAlignment(originalColumn, columns) &&
                                          "mr-0",
                                      )}
                                    >
                                      <SortButton
                                        columnId={columnId}
                                        sortState={sortState}
                                        handleSortChange={handleSortChange}
                                        column={header.column}
                                        className={cn(
                                          "h-7 w-7 shrink-0",
                                          !getColumnAlignment(originalColumn, columns) &&
                                            "mr-0",
                                        )}
                                      />
                                    </div>

                                    {/* Expand/collapse button for collapsed node_group columns */}
                                    {originalColumn.children &&
                                      originalColumn.children.length > 0 && (
                                        <div className="opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity">
                                          <ExpandCollapseButton
                                            isExpanded={false}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const isCurrentlyExpanded =
                                                expandedNodeGroups[columnId];
                                              setExpandedNodeGroups((prev) => ({
                                                ...prev,
                                                [columnId]: !prev[columnId],
                                              }));

                                              // If collapsing the parent, also collapse all child nodes
                                              if (isCurrentlyExpanded) {
                                                const childColumns =
                                                  originalColumn.children || [];
                                                const childExpandedUpdates: Record<
                                                  string,
                                                  boolean
                                                > = {};
                                                childColumns.forEach(
                                                  (childKey: string) => {
                                                    childExpandedUpdates[childKey] =
                                                      false;
                                                  },
                                                );
                                                setExpandedHeaders((prev) => ({
                                                  ...prev,
                                                  ...childExpandedUpdates,
                                                }));
                                              }
                                            }}
                                          />
                                        </div>
                                      )}
                                  </div>
                                  <div
                                    className={`flex gap-1 py-0 hover:text-none overflow-hidden min-w-0 items-center w-full ${
                                      getColumnAlignment(originalColumn, columns) ===
                                      "right"
                                        ? "order-2 ml-auto justify-end"
                                        : "order-1 justify-start"
                                    }`}
                                  >
                                    <div className="flex items-center gap-[6px] min-w-0">
                                      <SqTooltipProvider>
                                        <SqTooltip>
                                          <SqTooltipTrigger
                                            asChild
                                            className="cursor-pointer min-w-0"
                                          >
                                            <span className="truncate font-medium text-[12px] max-w-full block">
                                              {originalColumn.label}
                                            </span>
                                          </SqTooltipTrigger>
                                          <SqTooltipContent
                                            side="top"
                                            align="start"
                                            portal
                                          >
                                            {originalColumn.label}
                                          </SqTooltipContent>
                                        </SqTooltip>
                                      </SqTooltipProvider>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  onMouseDown={header.getResizeHandler()}
                                  onTouchStart={header.getResizeHandler()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                  className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                                />
                              </TableHead>
                            );
                          }
                        }

                        if (isExpanded && isSecondary) {
                          return null;
                        }
                        const colSpan = isExpanded && !isSecondary ? 3 : 1;
                        const isColumnSortable = sortableColumnIds.includes(columnId);
                        return (
                          <SortableTableHead
                            key={header.id}
                            id={columnId}
                            disabled={!isColumnSortable}
                            colSpan={colSpan}
                            style={{
                              ...getPinnedColumnStyle(header.column, true),
                              position: header.column.getIsPinned()
                                ? "sticky"
                                : "relative",
                              ...(index === 0 && { borderLeft: "none" }),
                              ...(isFirstRow && { borderTop: "none" }),
                              ...(index === headerGroup.headers.length - 1 &&
                                !showAddMetricButton && { borderRight: "none" }),
                            }}
                            className={`p-0 h-9 text-brand-primary-new/80 group-hover/row:text-brand-primary-new group/header ${!(index === headerGroup.headers.length - 1 && !showAddMetricButton) ? "border-r" : ""} border-[#E4E4E7] border-b ${header.column.getIsLastColumn("left") ? "pinned-last" : ""}
                         ${header.column.getIsPinned() ? "pinned-cell" : ""}`}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              onPointerDown={(e) => e.stopPropagation()}
                              className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                            />
                          </SortableTableHead>
                        );
                      })}
                      {/* Add Metric Button Column */}
                      {showAddMetricButton && (
                        <TableHead
                          className="p-0 h-9 border-b border-[#E4E4E7] bg-white border-r last:border-r-0 group-hover/row:!bg-[rgb(251,251,251)]"
                          style={{}}
                        >
                          <div className="w-min px-1 py-1 bg-white group-hover/row:!bg-[rgb(251,251,251)]">
                            <Metrics
                              selectedMetrics={selectedMetrics}
                              metricsOptions={metricOptions}
                              onMetricChange={(metrics) => onMetricChange?.(metrics)}
                              renderingStyle={"multiselect"}
                              trigger={
                                <SqButton
                                  size={"md"}
                                  variant={"icon"}
                                  bgColor="white"
                                  hoverBgColor="#FBFBFB"
                                  hasOutline
                                  className="h-8 w-8 flex items-center justify-center squircle-fill-[white]"
                                >
                                  <RiAddLargeLine
                                    size={14}
                                    color="#9AA3BD"
                                    className="shrink-0"
                                  />
                                </SqButton>
                              }
                            />
                          </div>
                        </TableHead>
                      )}
                    </TableRow>
                  ))}
                </SortableContext>
                {/* Sub-header row showing date and compare info; show only if any column expanded */}
                {anyExpanded && (
                  <TableRow className="group/row hover:bg-[rgb(251,251,251)] h-9">
                    {table.getHeaderGroups()[0]?.headers.map((header, index) => {
                      const colId = header.column.id;
                      const meta = header.column.columnDef
                        .meta as CreativeTableColumnMeta;
                      const isPrevious = colId.endsWith("_previous");
                      const isChange = colId.endsWith("_change");
                      const baseId = colId.replace(/(_previous|_change)$/, "");
                      const isExpanded = isColumnExpanded(colId, expandedHeaders);
                      const isSecondary = isColumnSecondary(colId);

                      // Check if this is a child node of an expanded node group
                      const baseColId = colId.replace(/(_previous|_change)$/, "");
                      const parentNodeGroup = columns.find(
                        (c) =>
                          (c.column_type as string) === "node_group" &&
                          c.children?.includes(baseColId),
                      );
                      const isNodeGroupChild =
                        parentNodeGroup && expandedNodeGroups[parentNodeGroup.key];

                      // Hide node_group columns when they are expanded (they'll be shown in the top row)
                      const isExpandedNodeGroup =
                        meta.type === "node_group" && expandedNodeGroups[colId];
                      if (isExpandedNodeGroup) {
                        return null;
                      }

                      // Skip secondary columns when expanded (they're handled by the main column)
                      // But allow child node compare columns to be handled separately
                      if (isExpanded && isSecondary && !isNodeGroupChild) {
                        return null;
                      }

                      // For child nodes of expanded groups, show dates if they are expanded
                      if (isNodeGroupChild) {
                        const isChildExpanded = expandedHeaders[baseColId]; // Check base column, not compare column
                        if (isChildExpanded) {
                          // Show date info for expanded child node columns using renderSubHeaderCell
                          if (isPrevious) {
                            return renderSubHeaderCell(
                              header.column,
                              colId,
                              formatDateRange({
                                ...reportMetadata?.compare_date,
                                timezone: selectedAccount?.time_zone ?? "UTC",
                              }),
                              false,
                              true, // showSortButton - only for expanded columns
                            );
                          } else if (isChange) {
                            return renderSubHeaderCell(
                              header.column,
                              colId,
                              "Change",
                              false,
                              true, // showSortButton - only for expanded columns
                            );
                          } else if (!isSecondary) {
                            // Main child node column - show current date range
                            return renderSubHeaderCell(
                              header.column,
                              colId,
                              formatDateRange({
                                ...reportMetadata?.date,
                                timezone: selectedAccount?.time_zone ?? "UTC",
                              }),
                              index === 0,
                              true, // showSortButton - only for expanded columns
                            );
                          }
                        } else {
                          // Show current date range for non-expanded child nodes
                          return renderSubHeaderCell(
                            header.column,
                            colId,
                            formatDateRange({
                              ...reportMetadata?.date,
                              timezone: selectedAccount?.time_zone ?? "UTC",
                            }),
                            index === 0,
                            false, // showSortButton - false for non-expanded columns
                          );
                        }
                      }

                      // If this is an expanded column, we need to render 3 sub-cells (main, previous, change)
                      if (isExpanded && !isSecondary) {
                        const baseColumn = header.column;
                        const previousColumn = table.getColumn(`${baseId}_previous`);
                        const changeColumn = table.getColumn(`${baseId}_change`);

                        return (
                          <>
                            {/* Main column sub-header */}
                            {renderSubHeaderCell(
                              baseColumn,
                              baseId,
                              formatDateRange({
                                ...reportMetadata?.date,
                                timezone: selectedAccount?.time_zone ?? "UTC",
                              }),
                              index === 0,
                              true, // showSortButton - always true for regular expanded columns
                            )}

                            {/* Previous column sub-header */}
                            {previousColumn &&
                              renderSubHeaderCell(
                                previousColumn,
                                `${baseId}_previous`,
                                formatDateRange({
                                  ...reportMetadata?.compare_date,
                                  timezone: selectedAccount?.time_zone ?? "UTC",
                                }),
                                false,
                                true, // showSortButton - always true for regular expanded columns
                              )}

                            {/* Change column sub-header */}
                            {changeColumn &&
                              renderSubHeaderCell(
                                changeColumn,
                                `${baseId}_change`,
                                "Change",
                                false,
                                true, // showSortButton - always true for regular expanded columns
                              )}
                          </>
                        );
                      }

                      // For non-expanded columns, render normally
                      return (
                        <TableHead
                          key={`sub-${header.id}`}
                          style={{
                            ...getPinnedColumnStyle(header.column, true),
                            borderBottom: "1px solid #E4E4E7",
                            position: header.column.getIsPinned() ? "sticky" : "relative",
                            ...(index === 0 && { borderLeft: "none" }),
                          }}
                          className={`p-0 h-9 border-r border-[#E4E4E7] last:border-r-0 ${header.column.getIsLastColumn("left") ? "pinned-last" : ""}
                       ${header.column.getIsPinned() ? "pinned-cell" : ""}`}
                        >
                          {meta?.type === "number" || meta?.type === "node_group" ? (
                            <div className="px-2 py-1 text-[12px] text-brand-primary-new/60 leading-[16px] flex items-center justify-end gap-2">
                              <div className="text-right">
                                {formatDateRange({
                                  ...reportMetadata?.date,
                                  timezone: selectedAccount?.time_zone ?? "UTC",
                                })}
                              </div>
                            </div>
                          ) : null}
                        </TableHead>
                      );
                    })}
                    {showAddMetricButton && <TableHead className="border-b"></TableHead>}
                  </TableRow>
                )}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length + splitData.misc.length ? (
                  <>
                    {table.getRowModel().rows.map((row, rowIndex) => {
                      const parentRow = row.original;
                      const hasBreakdowns =
                        parentRow.breakdowns &&
                        Array.isArray(parentRow.breakdowns) &&
                        parentRow.breakdowns.length > 0;
                      const ad = parentRow.ads?.[0] ?? parentRow.first_ad_metadata;
                      const mediaType = parentRow.creative_type;
                      const mediaItem: CreativeReportMediaItem | undefined =
                        getFirstMedia(ad)[0] ??
                        (parentRow.creative_thumbnail
                          ? {
                              type: parentRow.creative_type,
                              url: parentRow.creative_thumbnail,
                              thumbnail:
                                parentRow.creative_type === "video"
                                  ? parentRow.creative_thumbnail
                                  : undefined,
                            }
                          : undefined);
                      const getRightPanelAction = (
                        row: Partial<CreativeReportRow & CreativeReportAd>,
                      ) => {
                        return () => {
                          if (onOpenGroupSheetWithMode) {
                            onOpenGroupSheetWithMode(
                              row as CreativeReportRow,
                              "table_view",
                            );
                          } else {
                            if (setGroupSheetOpen) {
                              setGroupSheetOpen(true);
                            }
                            if (setSelectedAdGroup) {
                              setSelectedAdGroup(row as CreativeReportRow);
                            }
                          }
                          if (setPreviewMediaItem) {
                            setPreviewMediaItem(mediaItem);
                          }
                        };
                      };
                      const mediaItems =
                        parentRow.ads?.length > 0
                          ? parentRow.ads.flatMap((ad) => [...getFirstMedia(ad)])
                          : parentRow.first_ad_metadata
                            ? getFirstMedia(parentRow.first_ad_metadata)
                            : [];
                      const openGalleryAction = () => {
                        if (mediaItems && mediaItems.length > 0) {
                          const winningColumns =
                            ad_columns?.filter(
                              (col) =>
                                (
                                  col as CreativeReportColumn & {
                                    winning_criteria_name?: string;
                                  }
                                ).winning_criteria_name,
                            ) || [];

                          const mapAdsToGallery = (ads: CreativeReportAd[]) =>
                            ads.map((ad) => {
                              const winningCriteriaData: Record<string, boolean> = {};
                              winningColumns.forEach((col) => {
                                const value = (ad as Record<string, unknown>)[col.key];
                                if (typeof value === "boolean") {
                                  winningCriteriaData[col.key] = value;
                                }
                              });
                              return {
                                ...ad.creative,
                                type: ad.creative_type,
                                ad_name: ad.ad_name || "",
                                ad_id: ad.ad_id || "",
                                creative_id: ad.creative_id || "",
                                videoRetentionData:
                                  ad.creative_type === "video"
                                    ? computeRetentionData(ad)
                                    : undefined,
                                ...winningCriteriaData,
                              };
                            });

                          const groupVal = groupByKey
                            ? (() => {
                                const raw = (parentRow as Record<string, unknown>)[
                                  groupByKey
                                ];
                                return typeof raw === "string" || typeof raw === "number"
                                  ? String(raw)
                                  : "";
                              })()
                            : "";

                          const openWithItems = (
                            items: typeof galleryMediaItems,
                            total: number,
                          ) => {
                            setGalleryMediaItems(items);
                            galleryGroupValueRef.current = groupVal;
                            galleryTotalRef.current = total;
                            galleryNextPageRef.current = 2;
                            setCurrentGroup(
                              parentRow[labelConfigId ?? ""] as string | null,
                            );
                          };

                          if (parentRow.ads?.length > 0) {
                            openWithItems(
                              mapAdsToGallery(parentRow.ads),
                              Number(
                                (parentRow as Record<string, unknown>).total_ads ??
                                  parentRow.ads.length,
                              ),
                            );
                            setOpenGalleryView(true);
                          } else if (
                            fetchGalleryAds &&
                            groupVal &&
                            Number(
                              (parentRow as Record<string, unknown>).total_ads ?? 0,
                            ) > 1
                          ) {
                            setGalleryMediaItems([]);
                            setIsGalleryLoading(true);
                            setOpenGalleryView(true);
                            galleryGroupValueRef.current = groupVal;
                            fetchGalleryAds(groupVal, 1, 25)
                              .then((response) => {
                                setIsGalleryLoading(false);
                                if (
                                  response?.status === "success" &&
                                  response.ads.length > 0
                                ) {
                                  openWithItems(
                                    mapAdsToGallery(response.ads),
                                    response.pagination?.total_ads ?? response.ads.length,
                                  );
                                } else if (parentRow.first_ad_metadata) {
                                  openWithItems(
                                    mapAdsToGallery([parentRow.first_ad_metadata]),
                                    Number(
                                      (parentRow as Record<string, unknown>).total_ads ??
                                        1,
                                    ),
                                  );
                                }
                              })
                              .catch(() => {
                                setIsGalleryLoading(false);
                              });
                          } else if (parentRow.first_ad_metadata) {
                            openWithItems(
                              mapAdsToGallery([parentRow.first_ad_metadata]),
                              Number(
                                (parentRow as Record<string, unknown>).total_ads ?? 1,
                              ),
                            );
                            setOpenGalleryView(true);
                          }
                        }
                      };

                      // Helper function to render a breakdown row
                      const renderBreakdownRow = (
                        breakdown: Record<string, unknown>,
                        breakdownIndex: number,
                      ) => {
                        const breakdownRowData = {
                          ...breakdown,
                          // Keep parent row's ads for media display
                          ads: parentRow.ads,
                          ads_previous: parentRow.ads_previous,
                          creative_type: parentRow.creative_type,
                          creative: parentRow.creative,
                        } as CreativeReportRow;

                        return (
                          <TableRow
                            key={`${row.id}-breakdown-${breakdownIndex}`}
                            className="!bg-[#FAFAFA] !hover:bg-[#F5F5F5]"
                          >
                            {row.getVisibleCells().map((cell, cellIndex) => {
                              const breakdownValue =
                                breakdownRowData[
                                  cell.column.id as keyof CreativeReportRow
                                ];
                              const meta = cell.column.columnDef
                                .meta as CreativeTableColumnMeta;

                              return (
                                <TableCell
                                  key={`${cell.id}-breakdown-${breakdownIndex}`}
                                  style={{
                                    ...getPinnedColumnStyle(cell.column),
                                    ...(isColumnSecondary(cell.column.id) && {
                                      backgroundColor: "#F1F1F133",
                                    }),
                                    ...(cell.column.id === SPACER && { width: "100%" }),
                                    borderRight: "1px solid #E4E4E7",
                                    borderBottom: "1px solid #E4E4E7",
                                    ...(cellIndex === 0 && { borderLeft: "none" }),
                                    ...(cell.column.getIsLastColumn() &&
                                      !showAddMetricButton && {
                                        borderRight: "none",
                                      }),
                                  }}
                                  className={cn(
                                    "max-w-24 py-0 h-[28px] pl-2 pr-3 overflow-hidden relative !bg-[#FAFAFA] !hover:bg-[#F5F5F5]",
                                    "text-[12px] text-brand-primary-new/80 font-normal border-r",
                                    cell.column.getIsLastColumn("left") && "pinned-last",
                                    cell.column.getIsPinned() && "pinned-cell",
                                    `text-${meta?.align || "left"}`,
                                  )}
                                >
                                  {cell.column.getIsFirstColumn("left") ? (
                                    <div className="flex items-center gap-3 w-full relative overflow-hidden">
                                      <div className="pl-[72px] w-full flex items-center justify-start text-left leading-[20px]">
                                        {breakdownPrimaryColumnKey ? (
                                          <div className="w-max flex items-center justify-center truncate text-[12px] text-brand-primary-new/80 font-normal">
                                            {(() => {
                                              const value =
                                                breakdown[breakdownPrimaryColumnKey];
                                              if (value === null || value === undefined)
                                                return "";
                                              if (
                                                typeof value === "string" ||
                                                typeof value === "number"
                                              ) {
                                                return (
                                                  <RenderToolTippedCell
                                                    cellText={String(value)}
                                                    headerText={""}
                                                    align={"left"}
                                                    isPinned={false}
                                                    columnKey={cell.column.id}
                                                    className="line-clamp-1"
                                                  />
                                                );
                                              }
                                              if (Array.isArray(value)) {
                                                return value.map(String).join(", ");
                                              }
                                              return JSON.stringify(value);
                                            })()}
                                          </div>
                                        ) : (
                                          <div className="text-[12px] text-brand-primary-new/80 font-normal">
                                            {typeof breakdownValue === "string" ||
                                            typeof breakdownValue === "number"
                                              ? String(breakdownValue)
                                              : ""}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    (() => {
                                      // Render cell based on column type
                                      if (
                                        meta?.type === "number" ||
                                        meta?.dataType === "number"
                                      ) {
                                        const numValue = Number(breakdownValue);
                                        if (!isNaN(numValue)) {
                                          const totalAds = getTotalAdsForColumn(
                                            breakdownRowData,
                                            cell.column.id,
                                          );
                                          const formattedValue = formatValueWithSuffix(
                                            numValue,
                                            meta.prefix || "",
                                            meta.suffix || "",
                                            totalAds,
                                          );
                                          return (
                                            <RenderToolTippedCell
                                              cellText={formattedValue}
                                              headerText={meta.label || ""}
                                              align={meta.align || "right"}
                                              isPinned={cell.column.getIsPinned()}
                                              columnKey={cell.column.id}
                                              info={meta.info}
                                            />
                                          );
                                        }
                                      }

                                      return (
                                        <RenderToolTippedCell
                                          cellText={
                                            typeof breakdownValue === "string" ||
                                            typeof breakdownValue === "number"
                                              ? String(breakdownValue)
                                              : ""
                                          }
                                          headerText={meta?.label || ""}
                                          align={meta?.align || "left"}
                                          isPinned={cell.column.getIsPinned()}
                                          columnKey={cell.column.id}
                                          info={meta?.info}
                                        />
                                      );
                                    })()
                                  )}
                                </TableCell>
                              );
                            })}
                            {showAddMetricButton && (
                              <TableCell className="p-0 border-r border-b border-[#E4E4E7] last:border-r-0 bg-[#FAFAFA]"></TableCell>
                            )}
                          </TableRow>
                        );
                      };

                      // Helper function to render a loading shimmer row for breakdowns
                      const renderBreakdownLoadingRow = () => {
                        return (
                          <TableRow
                            key={`${row.id}-breakdown-loading`}
                            className="!bg-[#FAFAFA] !hover:bg-[#FAFAFA]"
                          >
                            {row.getVisibleCells().map((cell, cellIndex) => {
                              return (
                                <TableCell
                                  key={`${cell.id}-breakdown-loading`}
                                  style={{
                                    ...getPinnedColumnStyle(cell.column),
                                    ...(isColumnSecondary(cell.column.id) && {
                                      backgroundColor: "#F1F1F133",
                                    }),
                                    ...(cell.column.id === SPACER && { width: "100%" }),
                                    borderRight: "1px solid #E4E4E7",
                                    borderBottom: "1px solid #E4E4E7",
                                    ...(cellIndex === 0 && { borderLeft: "none" }),
                                    ...(cell.column.getIsLastColumn() &&
                                      !showAddMetricButton && {
                                        borderRight: "none",
                                      }),
                                  }}
                                  className={cn(
                                    "max-w-24 py-0 h-[28px] pl-3 pr-3 overflow-hidden relative !bg-[#FAFAFA] !hover:bg-[#FAFAFA]",
                                    cell.column.getIsLastColumn("left") && "pinned-last",
                                    cell.column.getIsPinned() && "pinned-cell",
                                  )}
                                >
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Skeleton className="max-w-[85px] h-[10px] w-full rounded-[4px]" />
                                  </div>
                                </TableCell>
                              );
                            })}
                            {showAddMetricButton && (
                              <TableCell className="p-0 border-r border-b border-[#E4E4E7] last:border-r-0 bg-[#FAFAFA]">
                                <div className="w-full h-full flex items-center justify-center px-2">
                                  <Skeleton className="max-w-[85px] h-[10px] w-full rounded-[4px]" />
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      };

                      return (
                        <React.Fragment key={row.id}>
                          <TableRow
                            data-state={row.getIsSelected() && "selected"}
                            className={cn(
                              "group/row hover:bg-[rgb(251,251,251)] transition-opacity duration-200",
                              isPaginationLoading && "opacity-50 pointer-events-none",
                            )}
                          >
                            {row.getVisibleCells().map((cell, cellIndex) => (
                              <TableCell
                                key={cell.id}
                                style={{
                                  ...getPinnedColumnStyle(cell.column),
                                  ...(getMainColumnBgColor(
                                    cell.column.id,
                                    row.original,
                                  ) && {
                                    backgroundColor: getMainColumnBgColor(
                                      cell.column.id,
                                      row.original,
                                    ),
                                  }),
                                  ...(isColumnSecondary(cell.column.id) && {
                                    backgroundColor: "#F1F1F133",
                                  }),
                                  ...(cell.column.id === SPACER && { width: "100%" }),
                                  borderRight: "1px solid #E4E4E7",
                                  borderBottom: "1px solid #E4E4E7",
                                  ...(cellIndex === 0 && { borderLeft: "none" }),
                                  ...(cell.column.getIsLastColumn() &&
                                    !showAddMetricButton && {
                                      borderRight: "none",
                                    }),
                                }}
                                className={cn(
                                  "max-w-24 py-2 pl-2 pr-3 overflow-hidden relative",
                                  "text-[12px] text-brand-primary-new/80 font-normal group-hover/row:text-brand-primary-new border-r",
                                  cell.column.getIsLastColumn("left") && "pinned-last",
                                  cell.column.getIsPinned() && "pinned-cell",
                                  `text-${(cell.column.columnDef.meta as CreativeTableColumnMeta)?.align || "left"}`,
                                )}
                              >
                                {cell.column.getIsFirstColumn("left") ? (
                                  overwritePrimaryCell ? (
                                    overwritePrimaryCell(cell)
                                  ) : (
                                    <div
                                      className={cn("flex items-center relative w-full")}
                                    >
                                      <RenderPinnedCell
                                        mediaItem={mediaItem}
                                        text={flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext(),
                                        )}
                                        onRightPanelClick={getRightPanelAction(parentRow)}
                                        openGalleryView={
                                          mediaItems && mediaItems.length > 0
                                            ? openGalleryAction
                                            : () =>
                                                onOpenGroupSheetWithMode?.(
                                                  parentRow,
                                                  "creative_view",
                                                )
                                        }
                                        total_ads={
                                          (parentRow as Record<string, unknown>)
                                            .total_ads != null
                                            ? Number(
                                                (parentRow as Record<string, unknown>)
                                                  .total_ads,
                                              )
                                            : (parentRow?.ads?.length ?? 0)
                                        }
                                        previous_total_ads={
                                          (parentRow as Record<string, unknown>)
                                            .total_ads_previous != null
                                            ? Number(
                                                (parentRow as Record<string, unknown>)
                                                  .total_ads_previous,
                                              )
                                            : (parentRow?.ads_previous?.length ??
                                              undefined)
                                        }
                                        mediaType={mediaType ?? "image"}
                                        hideGalleryAction={
                                          !(mediaItems && mediaItems?.length > 0)
                                        }
                                        viewMode={viewMode ?? "table_view"}
                                        rowIndex={rowIndex}
                                        hideRightPanelTrigger={
                                          hideRightPanelTrigger ?? false
                                        }
                                        uniqueLabels={uniqueLabels}
                                        showAdsText={showAdsText}
                                        sequenceData={(() => {
                                          // Find sequence data from row by looking for keys ending with "_sequences"
                                          const sequenceKey = Object.keys(parentRow).find(
                                            (key) =>
                                              key.endsWith("_sequences") &&
                                              key.startsWith(cell.column.id),
                                          );
                                          if (sequenceKey && parentRow[sequenceKey]) {
                                            const data = parentRow[sequenceKey];
                                            return data as Array<{
                                              label: string;
                                              order: number;
                                            }>;
                                          }
                                          return undefined;
                                        })()}
                                        allAdNames={allAdNames}
                                        sectionId={sectionId}
                                        creativeData={
                                          ad
                                            ? (() => {
                                                const thumbnailUrl =
                                                  ad.creative?.thumbnail_url ||
                                                  ad.creative?.images?.[0]
                                                    ?.permalink_url ||
                                                  ad.creative?.videos?.[0]?.thumbnail_url;
                                                const mediaUrl =
                                                  ad.creative?.images?.[0]
                                                    ?.permalink_url ||
                                                  ad.creative?.videos?.[0]
                                                    ?.permalink_url ||
                                                  "";
                                                return {
                                                  creative_id: ad.creative_id || "",
                                                  ad_id: ad.ad_id || "",
                                                  thumbnail_url:
                                                    typeof thumbnailUrl === "string"
                                                      ? thumbnailUrl
                                                      : undefined,
                                                  type: ad.creative_type || mediaType,
                                                  ad_name: ad.ad_name,
                                                  media_url: mediaUrl,
                                                };
                                              })()
                                            : undefined
                                        }
                                        groupByKey={groupByKey}
                                        sectionMetricLabels={sectionMetricLabels}
                                        columns={columns}
                                        rowData={parentRow}
                                        isAdsLoading={false}
                                      />
                                    </div>
                                  )
                                ) : (
                                    cell.column.columnDef.meta as CreativeTableColumnMeta
                                  ).type === "node" ? (
                                  (() => {
                                    const meta = cell.column.columnDef
                                      .meta as CreativeTableColumnMeta;
                                    const value = cell.getValue();
                                    const numValue = Number(value);

                                    if (meta.type === "node" && !isNaN(numValue)) {
                                      const isLoading = isCellLoading(
                                        cell.column.id,
                                        String(value),
                                      );
                                      if (isLoading) {
                                        return (
                                          <div className="w-full px-2 pt-1">
                                            <Skeleton className="h-[23px]" />
                                          </div>
                                        );
                                      }

                                      // Format the value with suffix (e.g., " / {total_ads} Ads")
                                      const totalAds = getTotalAdsForColumn(
                                        row.original,
                                        cell.column.id,
                                      );
                                      const formattedValue = formatValueWithSuffix(
                                        numValue,
                                        "",
                                        meta.suffix || "",
                                        totalAds,
                                      );

                                      // Get percent change for node columns
                                      const percent = (
                                        row.original as Record<string, unknown>
                                      )[`${cell.column.id}_percent_change`];
                                      const isPercentLoading = isCellLoading(
                                        `${cell.column.id}_percent_change`,
                                        typeof percent === "string" ||
                                          typeof percent === "number"
                                          ? String(percent)
                                          : "",
                                      );

                                      return (
                                        <RenderToolTippedCell
                                          cellText={
                                            <div className="flex flex-col items-end gap-1">
                                              <div className="text-[12px]">
                                                {formattedValue}
                                              </div>
                                              <PercentageText
                                                value={
                                                  percent as
                                                    | number
                                                    | string
                                                    | null
                                                    | undefined
                                                }
                                                isLoading={isPercentLoading}
                                              />
                                            </div>
                                          }
                                          headerText={meta.label || ""}
                                          align={meta.align || "left"}
                                          isPinned={cell.column.getIsPinned()}
                                          columnKey={cell.column.id}
                                          info={meta.info}
                                          prefix={meta?.prefix}
                                        />
                                      );
                                    }

                                    return (
                                      <RenderToolTippedCell
                                        cellText={flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext(),
                                        )}
                                        info={meta.info}
                                        isPinned={cell.column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={cell.column.id}
                                      />
                                    );
                                  })()
                                ) : (
                                    cell.column.columnDef.meta as CreativeTableColumnMeta
                                  ).type === "text_ai_description" ? (
                                  <RenderTextAiDescription
                                    cellText={flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                    align={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).align
                                    }
                                    headerText={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).label
                                    }
                                    columnKey={cell.column.id}
                                  />
                                ) : (
                                    cell.column.columnDef.meta as CreativeTableColumnMeta
                                  ).type === "text_ai_label" ? (
                                  <RenderTextAiLabel
                                    cellText={flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                    align={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).align
                                    }
                                    isPinned={cell.column.getIsPinned()}
                                    columnKey={cell.column.id}
                                    viewMode={
                                      labelViewByColumn[cell.column.id] || "multiple"
                                    }
                                    uniqueLabels={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).uniqueLabels
                                    }
                                    labelType={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).labelType
                                    }
                                  />
                                ) : (
                                    cell.column.columnDef.meta as CreativeTableColumnMeta
                                  ).type === "custom" ? (
                                  CustomCell ? (
                                    <CustomCell cell={cell} />
                                  ) : (
                                    <>
                                      {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                      )}
                                    </>
                                  )
                                ) : (
                                  <RenderToolTippedCell
                                    cellText={flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                    info={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).info
                                    }
                                    isPinned={cell.column.getIsPinned()}
                                    align={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).align
                                    }
                                    headerText={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      ).label
                                    }
                                    columnKey={cell.column.id}
                                    prefix={
                                      (
                                        cell.column.columnDef
                                          .meta as CreativeTableColumnMeta
                                      )?.prefix
                                    }
                                  />
                                )}
                              </TableCell>
                            ))}
                            {/* Add Metric Button Column Cell */}
                            {showAddMetricButton && (
                              <TableCell
                                className="p-0 border-r border-b border-[#E4E4E7] last:border-r-0 bg-white group-hover/row:bg-[rgb(251,251,251)]"
                                style={{
                                  maxWidth: "800px",
                                  minWidth: "200px",
                                }}
                              ></TableCell>
                            )}
                          </TableRow>
                          {/* Render breakdown rows as visual children */}
                          {!hideBreakdownRows &&
                            hasBreakdowns &&
                            parentRow.breakdowns?.map((breakdown, breakdownIndex) =>
                              renderBreakdownRow(breakdown, breakdownIndex),
                            )}
                          {/* Render loading shimmer row when breakdown is selected but data hasn't loaded or is loading */}
                          {!hideBreakdownRows &&
                            breakdownValue &&
                            !hasBreakdowns &&
                            renderBreakdownLoadingRow()}
                        </React.Fragment>
                      );
                    })}
                    {showMiscRow && miscTotalRow && (
                      <TableRow
                        className={cn(
                          "group/row hover:bg-[rgb(251,251,251)] transition-opacity duration-200",
                          isPaginationLoading && "opacity-50 pointer-events-none",
                        )}
                      >
                        {table.getFlatHeaders().map((header, columnIndex) => {
                          const column = header.column;
                          return (
                            <TableCell
                              key={`misc-${column.id}`}
                              style={{
                                ...getPinnedColumnStyle(column),
                                ...(getMainColumnBgColor(
                                  column.id,
                                  miscTotalRow as unknown as CreativeReportRow,
                                ) && {
                                  backgroundColor: getMainColumnBgColor(
                                    column.id,
                                    miscTotalRow as unknown as CreativeReportRow,
                                  ),
                                }),
                                ...(isColumnSecondary(column.id) && {
                                  backgroundColor: "#F1F1F133",
                                }),
                                borderRight: "1px solid #E4E4E7",
                                borderBottom: "1px solid #E4E4E7",
                              }}
                              className={`max-w-24 py-2 pl-2 pr-3 h-10  overflow-hidden 
                            border-r border-border-gray
                            text-brand-primary-new/80 group-hover/row:text-brand-primary-new
                            ${column.getIsPinned() ? "pinned-cell" : ""}
                            ${column.getIsLastColumn("left") ? "pinned-last" : ""}
                            text-${(column.columnDef.meta as CreativeTableColumnMeta).align}`}
                            >
                              {columnIndex === 0 ? (
                                <MiscellaneousPrimaryCell
                                  cellText="Miscellaneous Ads"
                                  onHide={() => setShowMiscRow(false)}
                                  onRightPanelClick={() => {
                                    setGroupSheetOpen?.(true);
                                    setSelectedAdGroup?.(miscTotalRow);
                                  }}
                                />
                              ) : (
                                (() => {
                                  const meta = column.columnDef
                                    .meta as CreativeTableColumnMeta;
                                  const colId = column.id;
                                  const raw = (miscTotalRow as Record<string, unknown>)[
                                    colId
                                  ];
                                  if (raw === "" || raw === undefined) {
                                    return (
                                      <RenderToolTippedCell
                                        cellText={""}
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }
                                  const isNumber =
                                    meta.type === "number" || meta.dataType === "number";
                                  const isNode = meta.type === "node";
                                  const isSecondary = isColumnSecondary(colId);
                                  const isChange = isColumnChange(colId);
                                  if (!isNumber && !isNode) {
                                    return (
                                      <RenderToolTippedCell
                                        cellText={""}
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }
                                  if (isSecondary) {
                                    const isLoadingSec = isCellLoading(
                                      colId,
                                      typeof raw === "string" || typeof raw === "number"
                                        ? String(raw)
                                        : "",
                                    );
                                    const num = Number(raw);
                                    const text = isLoadingSec
                                      ? "__LOADING__"
                                      : isNaN(num)
                                        ? typeof raw === "string"
                                          ? raw
                                          : ""
                                        : formatValueWithSuffix(
                                            num,
                                            meta.prefix,
                                            meta.suffix,
                                            undefined,
                                            isChange, // isChangeColumn
                                          );
                                    return (
                                      <RenderToolTippedCell
                                        cellText={text}
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }

                                  // Handle node columns
                                  if (isNode) {
                                    const isLoadingNode = isCellLoading(
                                      colId,
                                      typeof raw === "string" || typeof raw === "number"
                                        ? String(raw)
                                        : "",
                                    );
                                    const num = Number(raw);
                                    const totalAds = (
                                      miscTotalRow as Record<string, unknown>
                                    ).total_ads as number | null | undefined;
                                    const formattedValue = isLoadingNode
                                      ? "__LOADING__"
                                      : isNaN(num)
                                        ? typeof raw === "string"
                                          ? raw
                                          : ""
                                        : formatValueWithSuffix(
                                            num,
                                            "",
                                            meta.suffix || "",
                                            totalAds,
                                          );

                                    return (
                                      <RenderToolTippedCell
                                        cellText={formattedValue}
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }

                                  // Handle collapsed node_group columns
                                  if (
                                    meta.type === "node_group" &&
                                    !expandedNodeGroups[colId]
                                  ) {
                                    const defaultKey = columns.find(
                                      (c) => c.key === colId,
                                    )?.default_key;
                                    if (defaultKey) {
                                      const childValue = (
                                        miscTotalRow as Record<string, unknown>
                                      )[defaultKey];
                                      const childColumn = columns.find(
                                        (c) => c.key === defaultKey,
                                      );

                                      if (
                                        childColumn &&
                                        childColumn.data_type === "number"
                                      ) {
                                        const num = Number(childValue);
                                        if (!isNaN(num)) {
                                          const totalAds = (
                                            miscTotalRow as Record<string, unknown>
                                          ).total_ads as number | null | undefined;
                                          const formattedValue = formatValueWithSuffix(
                                            num,
                                            childColumn?.prefix || "",
                                            childColumn?.suffix || "",
                                            totalAds,
                                          );

                                          return (
                                            <RenderToolTippedCell
                                              cellText={formattedValue}
                                              info={meta.info}
                                              isPinned={column.getIsPinned()}
                                              align="right"
                                              headerText={meta.label}
                                              columnKey={colId}
                                            />
                                          );
                                        }
                                      }

                                      return (
                                        <RenderToolTippedCell
                                          cellText={JSON.stringify(childValue || "—")}
                                          info={meta.info}
                                          isPinned={column.getIsPinned()}
                                          align={meta.align}
                                          headerText={meta.label}
                                          columnKey={colId}
                                        />
                                      );
                                    }

                                    return (
                                      <RenderToolTippedCell
                                        cellText="—"
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }
                                  const baseLoading = isCellLoading(
                                    colId,
                                    typeof raw === "string" || typeof raw === "number"
                                      ? String(raw)
                                      : "",
                                  );
                                  if (baseLoading)
                                    return (
                                      <div className="w-full px-2 pt-1">
                                        <Skeleton className="h-[23px]" />
                                      </div>
                                    );
                                  const numValue = Number(raw);
                                  const valueText = isNaN(numValue)
                                    ? typeof raw === "string"
                                      ? raw
                                      : ""
                                    : formatNumberWithPrefixSuffix(
                                        numValue,
                                        meta.prefix,
                                        meta.suffix,
                                      );
                                  const percentRaw = (
                                    miscTotalRow as Record<string, unknown>
                                  )[`${colId}_percent_change`];
                                  const isPercentLoading = isCellLoading(
                                    `${colId}_percent_change`,
                                    typeof percentRaw === "string" ||
                                      typeof percentRaw === "number"
                                      ? String(percentRaw)
                                      : "",
                                  );
                                  return (
                                    <div className="flex flex-col items-end gap-1">
                                      <div className="text-[12px]">
                                        {valueText === "N/A" ? "—" : valueText}
                                      </div>
                                      <PercentageText
                                        value={
                                          percentRaw as number | string | null | undefined
                                        }
                                        isLoading={isPercentLoading}
                                      />
                                    </div>
                                  );
                                })()
                              )}
                            </TableCell>
                          );
                        })}
                        {/* Add Metric Button Column Cell for Misc Row */}
                        {showAddMetricButton && (
                          <TableCell
                            className="p-0 border-[#E4E4E7] border-r last:border-r-0 bg-white group-hover/row:bg-[rgb(251,251,251)]"
                            style={{
                              maxWidth: "800px",
                              minWidth: "200px",
                            }}
                          ></TableCell>
                        )}
                      </TableRow>
                    )}
                    {totalRow && (
                      <TableRow className="group/row hover:bg-[rgb(251,251,251)]">
                        {table.getFlatHeaders().map((header, columnIndex) => {
                          const column = header.column;
                          return (
                            <TableCell
                              key={column.id}
                              style={{
                                ...getPinnedColumnStyle(column),
                                ...(isColumnSecondary(column.id) && {
                                  backgroundColor: "#F1F1F133",
                                }),

                                height: "60px",
                              }}
                              className={`max-w-24 py-2 pl-2 pr-3 font-medium overflow-hidden
                             border-r border-border-gray
                            text-brand-primary-new/80 group-hover/row:text-brand-primary-new
                            ${columnIndex === 0 ? "rounded-bl-md" : ""} 
                            ${column.getIsPinned() ? "pinned-cell" : ""}
                            ${column.getIsLastColumn("left") ? "pinned-last" : ""}
                            text-${(column.columnDef.meta as CreativeTableColumnMeta).align}`}
                            >
                              {columnIndex === 0 ? (
                                <div className="h-full flex items-center gap-2">
                                  <RenderToolTippedCell
                                    cellText="Net Results"
                                    isPinned={false}
                                    align="left"
                                    headerText={""}
                                    columnKey=""
                                    className="line-clamp-none flex items-center gap-1 [&>span]:flex-none"
                                    additionalInfo={
                                      labelView === "multiple"
                                        ? "Account total, counting each ad once, even if in multiple groups."
                                        : undefined
                                    }
                                  />
                                  {!showMiscRow && splitData.misc.length > 0 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <CommonButton
                                            variant="outline"
                                            size="sm"
                                            shadow="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowMiscRow(true);
                                            }}
                                            className="h-7 w-7"
                                          >
                                            <RiArrowUpWideLine
                                              size={14}
                                              color="#9AA3BD"
                                            />
                                          </CommonButton>
                                        </TooltipTrigger>
                                        <Portal>
                                          <TooltipContent
                                            className="squircle squircle-mask squircle-fill-[black] text-white text-[12px] py-2 px-3 min-w-0 max-w-[240px]"
                                            side="top"
                                            align="start"
                                            sideOffset={5}
                                          >
                                            Miscellaneous Ads
                                          </TooltipContent>
                                        </Portal>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              ) : (
                                (() => {
                                  const meta = column.columnDef
                                    .meta as CreativeTableColumnMeta;
                                  const colId = column.id;
                                  const defaultKey = columns.find(
                                    (c) => c.key === colId,
                                  )?.default_key;
                                  const raw = defaultKey
                                    ? totalRow[defaultKey as keyof typeof totalRow]
                                    : totalRow[colId as keyof typeof totalRow];
                                  if (raw === "" || raw === undefined) {
                                    return (
                                      <RenderToolTippedCell
                                        cellText={undefined}
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }
                                  const isNumber =
                                    meta.type === "number" || meta.dataType === "number";
                                  const isNode = meta.type === "node";
                                  const isSecondary = isColumnSecondary(colId);
                                  const isChange = isColumnChange(colId);
                                  if (!isNumber && !isNode) {
                                    const isLoadingText = isCellLoading(
                                      colId,
                                      typeof raw === "string" || typeof raw === "number"
                                        ? String(raw)
                                        : "",
                                    );
                                    const safeText = isLoadingText
                                      ? "__LOADING__"
                                      : typeof raw === "string"
                                        ? raw?.toUpperCase?.() === "UNDEFINED"
                                          ? ""
                                          : raw
                                        : typeof raw === "number"
                                          ? String(raw)
                                          : "";
                                    return (
                                      <RenderToolTippedCell
                                        cellText={safeText}
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }
                                  if (isSecondary) {
                                    const isLoadingSec = isCellLoading(
                                      colId,
                                      typeof raw === "string" || typeof raw === "number"
                                        ? String(raw)
                                        : "",
                                    );
                                    const num = Number(raw);
                                    const totalAds = getTotalAdsForColumn(
                                      totalRow,
                                      colId,
                                    );
                                    const text = isLoadingSec
                                      ? "__LOADING__"
                                      : raw === undefined || raw === null
                                        ? ""
                                        : !isNaN(num)
                                          ? formatValueWithSuffix(
                                              num,
                                              meta.prefix || "",
                                              meta.suffix || "",
                                              totalAds,
                                              isChange, // isChangeColumn
                                            )
                                          : typeof raw === "string"
                                            ? raw?.toUpperCase?.() === "UNDEFINED"
                                              ? ""
                                              : raw
                                            : "";

                                    return (
                                      <RenderToolTippedCell
                                        cellText={text}
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }

                                  // Handle node columns
                                  if (isNode) {
                                    const isLoadingNode = isCellLoading(
                                      colId,
                                      typeof raw === "string" || typeof raw === "number"
                                        ? String(raw)
                                        : "",
                                    );
                                    const num = Number(raw);
                                    const totalAds = totalRow.total_ads as
                                      | number
                                      | null
                                      | undefined;
                                    const formattedValue = isLoadingNode
                                      ? "__LOADING__"
                                      : raw === undefined || raw === null
                                        ? ""
                                        : !isNaN(num)
                                          ? formatValueWithSuffix(
                                              num,
                                              "",
                                              meta.suffix || "",
                                              totalAds,
                                            )
                                          : typeof raw === "string"
                                            ? raw?.toUpperCase?.() === "UNDEFINED"
                                              ? ""
                                              : raw
                                            : "";

                                    // Get percent change for node columns
                                    const percent =
                                      totalRow[
                                        `${colId}_percent_change` as keyof typeof totalRow
                                      ];

                                    const isPercentLoading = isCellLoading(
                                      `${colId}_percent_change`,
                                      typeof percent === "string" ||
                                        typeof percent === "number"
                                        ? String(percent)
                                        : "",
                                    );

                                    return (
                                      <RenderToolTippedCell
                                        cellText={
                                          <div className="flex flex-col items-end gap-1">
                                            <div className="text-[12px]">
                                              {formattedValue}
                                            </div>
                                            <PercentageText
                                              value={
                                                percent as
                                                  | number
                                                  | string
                                                  | null
                                                  | undefined
                                              }
                                              isLoading={isPercentLoading}
                                            />
                                          </div>
                                        }
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                        prefix={meta?.prefix}
                                      />
                                    );
                                  }

                                  // Handle collapsed node_group columns
                                  if (
                                    meta.type === "node_group" &&
                                    !expandedNodeGroups[colId]
                                  ) {
                                    const defaultKey = columns.find(
                                      (c) => c.key === colId,
                                    )?.default_key;
                                    if (defaultKey) {
                                      const childValue =
                                        totalRow[defaultKey as keyof typeof totalRow];
                                      const childColumn = columns.find(
                                        (c) => c.key === defaultKey,
                                      );

                                      if (
                                        childColumn &&
                                        childColumn.data_type === "number"
                                      ) {
                                        const num = Number(childValue);
                                        if (!isNaN(num)) {
                                          const totalAds = totalRow.total_ads as
                                            | number
                                            | null
                                            | undefined;
                                          const formattedValue = formatValueWithSuffix(
                                            num,
                                            childColumn?.prefix || "",
                                            childColumn?.suffix || "",
                                            totalAds,
                                          );

                                          // Get percent change for the default key column
                                          const percent =
                                            totalRow[
                                              `${defaultKey}_percent_change` as keyof typeof totalRow
                                            ];
                                          const isPercentLoading = isCellLoading(
                                            `${defaultKey}_percent_change`,
                                            typeof percent === "string" ||
                                              typeof percent === "number"
                                              ? String(percent)
                                              : "",
                                          );

                                          return (
                                            <RenderToolTippedCell
                                              cellText={
                                                <div className="flex flex-col items-end gap-1">
                                                  <div className="text-[12px]">
                                                    {formattedValue}
                                                  </div>
                                                  <PercentageText
                                                    value={
                                                      percent as
                                                        | number
                                                        | string
                                                        | null
                                                        | undefined
                                                    }
                                                    isLoading={isPercentLoading}
                                                  />
                                                </div>
                                              }
                                              info={meta.info}
                                              isPinned={column.getIsPinned()}
                                              align="right"
                                              headerText={meta.label}
                                              columnKey={colId}
                                            />
                                          );
                                        }
                                      }

                                      return (
                                        <RenderToolTippedCell
                                          cellText={JSON.stringify(childValue || "—")}
                                          info={meta.info}
                                          isPinned={column.getIsPinned()}
                                          align={meta.align}
                                          headerText={meta.label}
                                          columnKey={colId}
                                        />
                                      );
                                    }

                                    return (
                                      <RenderToolTippedCell
                                        cellText="—"
                                        info={meta.info}
                                        isPinned={column.getIsPinned()}
                                        align={meta.align}
                                        headerText={meta.label}
                                        columnKey={colId}
                                      />
                                    );
                                  }
                                  // Main metric total: value + percent change stacked
                                  const isLoadingBase = isCellLoading(
                                    colId,
                                    typeof raw === "string" || typeof raw === "number"
                                      ? String(raw)
                                      : "",
                                  );
                                  if (isLoadingBase) {
                                    return (
                                      <div className="w-full px-2 pt-1">
                                        <Skeleton className="h-[23px]" />
                                      </div>
                                    );
                                  }
                                  const numValue = Number(raw);
                                  const totalAds = totalRow.total_ads as
                                    | number
                                    | null
                                    | undefined;
                                  const valueText = isLoadingBase
                                    ? "__LOADING__"
                                    : raw === undefined || raw === null
                                      ? ""
                                      : !isNaN(numValue)
                                        ? formatValueWithSuffix(
                                            numValue,
                                            meta.prefix || "",
                                            meta.suffix || "",
                                            totalAds,
                                          )
                                        : typeof raw === "string"
                                          ? raw?.toUpperCase?.() === "UNDEFINED"
                                            ? ""
                                            : raw
                                          : "";
                                  const percentRaw =
                                    totalRow[
                                      `${colId}_percent_change` as keyof typeof totalRow
                                    ];
                                  const isLoadingPct = isCellLoading(
                                    `${colId}_percent_change`,
                                    typeof percentRaw === "string" ||
                                      typeof percentRaw === "number"
                                      ? String(percentRaw)
                                      : "",
                                  );

                                  return (
                                    <div className="flex flex-col items-end gap-1">
                                      <div className="text-[12px]">
                                        {valueText === "N/A" ? "—" : valueText}
                                      </div>
                                      <PercentageText
                                        value={
                                          percentRaw as number | string | null | undefined
                                        }
                                        isLoading={isLoadingPct}
                                      />
                                    </div>
                                  );
                                })()
                              )}
                            </TableCell>
                          );
                        })}
                        {/* Add Metric Button Column Cell for Total Row */}
                        {showAddMetricButton && (
                          <TableCell
                            className="p-0 border-[#E4E4E7] border-r last:border-r-0 bg-white group-hover/row:bg-[rgb(251,251,251)]"
                            style={{
                              maxWidth: "800px",
                              minWidth: "200px",
                            }}
                          ></TableCell>
                        )}
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        table.getAllColumns().length + (showAddMetricButton ? 1 : 0)
                      }
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {showPaginationIfPagination ? (
            table.getPageCount() > 1 ? (
              <DataTablePagination
                table={table}
                className="flex justify-between w-full text-brand-primary-new"
                buttonClass="hover:text-brand-primary-new h-8 w-8"
                viewMode={viewMode}
                isUserInitiatedPaginationChanges={isUserInitiatedPaginationChanges}
                paginationTitle={paginationTitle}
                userPageSize={userPageSize}
                hidePageSizeSelector={hidePageSizeSelector}
              />
            ) : null
          ) : (
            <DataTablePagination
              table={table}
              className="flex justify-between w-full text-brand-primary-new"
              buttonClass="hover:text-brand-primary-new h-8 w-8"
              viewMode={viewMode}
              isUserInitiatedPaginationChanges={isUserInitiatedPaginationChanges}
              paginationTitle={paginationTitle}
              userPageSize={userPageSize}
              hidePageSizeSelector={hidePageSizeSelector}
            />
          )}
          <CreativeGalleryPreview
            open={openGalleryView}
            loading={isGalleryLoading}
            onOpenChange={(open) => {
              setOpenGalleryView(open);
              if (!open) setIsGalleryLoading(false);
            }}
            creatives={galleryMediaItems}
            groupType={groupType}
            currentGroup={currentGroup}
            creativeType={creativeType}
            labelView={labelView}
            labelConfigId={labelConfigId}
            accountId={accountId}
            disableLabelManagement={disableLabelManagement}
            enableSearch={galleryMediaItems.length > 4}
            ad_columns={ad_columns}
            totalCreatives={galleryTotalCreatives}
            onLoadMore={fetchGalleryAds ? handleGalleryLoadMore : undefined}
          />
        </div>
        <DragOverlay dropAnimation={{ duration: 150, easing: "ease" }}>
          {activeDragColumn
            ? (() => {
                const isRight = getColumnAlignment(activeDragColumn, columns) === "right";
                return (
                  <div
                    className="h-9 bg-white border border-[#E4E4E7] flex items-center text-brand-primary-new/80"
                    style={{
                      width: activeDragWidth || undefined,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.10), 0 1px 4px rgba(0, 0, 0, 0.06)",
                    }}
                  >
                    <div className="flex items-center w-full pl-2 pr-3 min-w-0">
                      <div className={`flex ${isRight ? "order-1" : "order-2 ml-auto"}`}>
                        <RiDraggable
                          size={16}
                          className="text-brand-primary-400 shrink-0 mr-1"
                        />
                      </div>
                      <div
                        className={`flex gap-1 py-0 overflow-hidden min-w-0 items-center w-full ${
                          isRight
                            ? "order-2 ml-auto justify-end"
                            : "order-1 justify-start"
                        }`}
                      >
                        <div className="flex items-center gap-[6px] min-w-0">
                          {activeDragColumn?.sub_type === "EXTERNAL" && (
                            <IconDatabase
                              size={16}
                              color="#02185999"
                              className="shrink-0"
                            />
                          )}
                          <span className="truncate font-medium text-[12px] max-w-full block">
                            {activeDragColumn.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()
            : null}
        </DragOverlay>
      </DndContext>
    );
  },
);

CreativeTable.displayName = "CreativeTable";

export default CreativeTable;
